/**
 * UIManager - Liquid glass UI for VR navigation using HTML overlay + 3D GUI for VR
 */

import { Logger } from '../utils/Logger';
import { Scene } from '@babylonjs/core/scene';
import { SceneManager } from '../scenes/SceneManager';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { Rectangle } from '@babylonjs/gui/2D/controls/rectangle';
import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';
import { Control } from '@babylonjs/gui/2D/controls/control';
import { StackPanel } from '@babylonjs/gui/2D/controls/stackPanel';
import { Button } from '@babylonjs/gui/2D/controls/button';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { VideoTexture } from '@babylonjs/core/Materials/Textures/videoTexture';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
// Side-effect import: registers createDynamicTexture on engine (required for AdvancedDynamicTexture.CreateForMesh)
import '@babylonjs/core/Materials/Textures/dynamicTexture';
import '@babylonjs/core/Meshes/mesh.vertexData';
import { DeviceDetector } from '../utils/DeviceDetector';
import { HotspotInfoPanel } from './HotspotInfoPanel';
import { NavigationManager } from './NavigationManager';
import { LeadCaptureForm } from './LeadCaptureForm';
import { LeadCaptureFormVR } from './LeadCaptureFormVR';
import { getTileLayerConfig } from '../config/mapTileLayers';
import type { IAnalyticsService } from '../services/interfaces';
import type { XRHelper } from '../utils/XRHelper';
// WebXR types for controller handling
import type { WebXRInputSource } from '@babylonjs/core/XR/webXRInputSource';
import type { WebXRAbstractMotionController } from '@babylonjs/core/XR/motionController/webXRAbstractMotionController';
import type { WebXRControllerComponent } from '@babylonjs/core/XR/motionController/webXRControllerComponent';
import type { IWebXRMotionControllerAxesValue } from '@babylonjs/core/XR/motionController/webXRControllerComponent';

import { LoadingUI } from './LoadingUI';

// Import shared types from centralized location
import type {
  TextureVariant,
  SkyboxConfig,
  ModelConfig,
  PlaneConfig,
  LightConfig,
  HotspotConfig,
  ExperienceStage,
  ExperienceConfig,
  Vector3Config,
} from '../types';

// Re-export types for backward compatibility with existing imports
export type {
  TextureVariant,
  SkyboxConfig,
  ModelConfig,
  PlaneConfig,
  LightConfig,
  HotspotConfig,
  ExperienceStage,
  ExperienceConfig,
  Vector3Config,
};

// Leaflet types are defined in src/types/leaflet.d.ts (loaded dynamically from CDN)

const logger = Logger.getLogger('UIManager');

export class UIManager {
  private scene: Scene;
  private sceneManager: SceneManager;
  private loadingUI: LoadingUI;
  public analyticsService?: IAnalyticsService; // Injected from main.ts

  private experienceConfig: ExperienceConfig | null = null;
  private currentStageIndex: number = 0;

  // HTML UI Controls
  private navControls: HTMLElement | null = null;
  private prevButton: HTMLButtonElement | null = null;
  private nextButton: HTMLButtonElement | null = null;
  private sceneNameLabel: HTMLElement | null = null;
  private progressBarContainer: HTMLElement | null = null;
  private progressBarFill: HTMLElement | null = null;
  // private loadingScreen: HTMLElement | null = null; // Replaced by LoadingUI
  private stageCounterInline: HTMLElement | null = null;
  private stageSelector: HTMLElement | null = null;
  private stageList: HTMLElement | null = null;
  private closeSelectorBtn: HTMLButtonElement | null = null;
  private stageSelectorTabs: HTMLElement | null = null;
  private stageSelectorMapContainer: HTMLElement | null = null;
  private stageSelectorFloorplanContainer: HTMLElement | null = null;
  private stageSelectorMap: L.Map | null = null; // Leaflet map instance
  private controlButtonsContainer: HTMLElement | null = null;
  private vrControlsToggle: HTMLElement | null = null;
  private vrControlsInfo: HTMLElement | null = null;
  private fullscreenBtn: HTMLButtonElement | null = null;
  private gazeModeBtn: HTMLButtonElement | null = null;
  private enterVRButton: HTMLButtonElement | null = null;
  private playPauseBtn: HTMLButtonElement | null = null;
  private playPauseIcon: HTMLElement | null = null;
  private videoControls: HTMLElement | null = null;
  private audioControls: HTMLElement | null = null;
  private audioMuteBtn: HTMLButtonElement | null = null;
  private audioVolumeSlider: HTMLInputElement | null = null;
  private audioTitlesDisplay: HTMLElement | null = null;
  private backgroundAudioTitle: HTMLElement | null = null;
  private spatialAudioTitle: HTMLElement | null = null;
  private videoAudioTitle: HTMLElement | null = null;
  private currentBackgroundAudio: string | null = null;
  private currentSpatialAudio: string | null = null;
  private currentVideoAudio: string | null = null;
  private tapToHearAudioOverlay: HTMLElement | null = null;
  private dragToLookHelper: HTMLElement | null = null;
  private hasUserInteractedForAudio: boolean = false;
  private hasUserDraggedToLook: boolean = false;
  private dragToLookTimeout: ReturnType<typeof setTimeout> | null = null;
  private isVrControlsInfoOpen: boolean = false;
  private floorArrowTooltip: HTMLElement | null = null;
  private showFloorArrowTooltips: boolean = false;
  private isStageSelectorOpen: boolean = false;
  private isLoading: boolean = false;
  private hasInitialLoadCompleted: boolean = false;
  private isFreeLookPortalMode: boolean = false; // Flashlight mode active state
  private disableTransitions: boolean = false; // Skip portal animations between stages
  private disableGazeNavigation: boolean = false; // Hide flashlight button and disable x-ray portal effect
  private preloadedStages: Map<number, boolean> = new Map();
  private progressUpdateInterval: number | null = null;


  // 3D GUI for VR
  private vrGuiPlane: Mesh | null = null;
  private vrGuiTexture: AdvancedDynamicTexture | null = null;
  private vrGuiPanel: Rectangle | null = null;
  private vrStageList: StackPanel | null = null;
  private vrMenuHoveredIndex: number = -1;

  // VR Loading Indicator
  private vrLoadingPlane: Mesh | null = null;
  private vrLoadingTexture: AdvancedDynamicTexture | null = null;
  private vrLoadingPanel: Rectangle | null = null;
  private vrLoadingText: TextBlock | null = null;
  private vrLoadingSpinner: TextBlock | null = null;
  private vrLoadingAnimationFrame: number = 0;

  // Hand Tracking Navigation Panel
  private handNavPlane: Mesh | null = null;
  private handNavTexture: AdvancedDynamicTexture | null = null;
  private handNavVisible: boolean = false;
  private handTrackingActive: boolean = false;
  private activeHandInputSources: Set<string> = new Set();

  // Hotspot Info Panel
  private hotspotInfoPanel: HotspotInfoPanel;

  // Lead Capture Forms
  private leadCaptureForm: LeadCaptureForm | null = null;
  private leadCaptureFormVR: LeadCaptureFormVR | null = null;
  private leadFormSequenceId: string | null = null; // Stored for lead form context
  private leadFormSessionId: string | null = null; // Stored for lead form context

  // Event handlers (stored for cleanup)
  private minimapClickHandler: ((e: Event) => void) | null = null;
  private prevButtonHandler: (() => void) | null = null;
  private nextButtonHandler: (() => void) | null = null;

  // Navigation Manager (floor plans and maps)
  private navigationManager: NavigationManager | null = null;

  constructor(scene: Scene, sceneManager: SceneManager, loadingUI: LoadingUI) {
    this.scene = scene;
    this.sceneManager = sceneManager;
    this.loadingUI = loadingUI;

    // Create hotspot info panel
    this.hotspotInfoPanel = new HotspotInfoPanel(scene, this.sceneManager.getHotspotComponent());

    // Setup hotspot event handlers
    this.setupHotspotHandlers();

    // Log device info for debugging
    DeviceDetector.logDeviceInfo();

    // Listen for VR state changes to update UI
    this.setupVRStateListener();
  }

  /**
   * Listen for VR state changes to handle UI transitions
   */
  private setupVRStateListener(): void {
    // We need to wait for XRHelper to be initialized in main.ts
    // But we can poll or use a global event if needed.
    // Better approach: The main.ts should pass the XRHelper instance or we can access it via scene
    
    // For now, we'll check periodically if we're in VR when the panel is open
    // A more robust solution would be to have XRHelper emit an event we can listen to
    
    // Ideally, we would subscribe to XRHelper.onVRStateChanged here
    // Since we don't have direct access to the XRHelper instance created in main.ts,
    // we'll rely on the fact that main.ts sets up the XRHelper.
    
    // Let's add a method that main.ts can call to register the callback
  }

  /**
   * Called by main.ts when XRHelper is initialized
   */
  public setXRHelper(xrHelper: XRHelper): void {
    xrHelper.onVRStateChanged = (isInVR: boolean) => {
      this.onVRStateChanged(isInVR);
    };
  }

  /**
   * Handle VR state change
   */
  private onVRStateChanged(isInVR: boolean): void {
    logger.info(`VR State changed: ${isInVR ? 'Entered VR' : 'Exited VR'}`);
    
    // 1. Handle Hotspot Info Panel
    const currentHotspot = this.hotspotInfoPanel.getCurrentHotspot();
    if (currentHotspot && this.hotspotInfoPanel.isVisible()) {
      logger.debug('Re-opening info panel for new mode');
      // Hide current panel
      this.hotspotInfoPanel.hide();
      
      // Re-show in new mode after a short delay to ensure scene is ready
      setTimeout(() => {
        this.hotspotInfoPanel.show(currentHotspot, isInVR);
      }, 100);
    }

    // 2. Hide hand tracking nav when exiting VR
    if (!isInVR) {
      this.activeHandInputSources.clear();
      this.handTrackingActive = false;
      this.setHandTrackingNavVisible(false);
    }

    // 3. Handle Stage Selector
    if (this.isStageSelectorOpen) {
      if (isInVR) {
        this.closeStageSelector(); // Close 2D
        this.openVRStageSelector(); // Open VR
      } else {
        this.closeVRStageSelector(); // Close VR
        this.openStageSelector(); // Open 2D
      }
    }

    // 3. Handle Dome Rotation for VR
    // In VR, the camera is rotated 180 degrees, so we need to rotate the dome to match
    const domeComponent = this.sceneManager.getDomeComponent();
    if (domeComponent) {
      if (isInVR) {
        domeComponent.rotate(Math.PI);
      } else {
        domeComponent.rotate(-Math.PI);
      }
    }

    // 4. Update hotspot billboard mode
    // Desktop: BILLBOARDMODE_ALL (full billboard)
    // VR: BILLBOARDMODE_Y (Y-axis only, stays upright)
    const hotspotComponent = this.sceneManager.getHotspotComponent();
    if (hotspotComponent) {
      hotspotComponent.setVRBillboardMode(isInVR);
    }
  }

  /**
   * Setup hotspot interaction handlers
   */
  private setupHotspotHandlers(): void {
    // Disable dome picking in viewer mode so hotspot clicks aren't intercepted
    const dome = this.sceneManager.getDomeComponent().getDomeMesh();
    if (dome) {
      dome.isPickable = false;
    }

    const hotspotComponent = this.sceneManager.getHotspotComponent();

    // Handle hotspot click
    hotspotComponent.onHotspotClick = (hotspot: HotspotConfig) => {
      logger.debug('Hotspot clicked:', hotspot.id, 'Type:', hotspot.type);

      // Handle navigation hotspots
      if (hotspot.type === 'navigation' && hotspot.targetStageId) {
        // Prevent spam clicking during transition
        if (this.isLoading) {
          logger.debug('Already transitioning, ignoring click');
          return;
        }

        logger.debug(`Navigating to stage: ${hotspot.targetStageId}`);

        // Find target stage index and update UI immediately
        if (this.experienceConfig) {
          const targetIndex = this.experienceConfig.stages.findIndex(s => s.id === hotspot.targetStageId);
          if (targetIndex >= 0) {
            // Lock navigation during transition
            this.isLoading = true;

            // Show loader on navigation controls during portal transition
            this.setNavigationEnabled(false);

            const previousIndex = this.currentStageIndex;
            this.currentStageIndex = targetIndex;
            const targetStage = this.experienceConfig.stages[targetIndex];
            this.updateSceneNameLabel(targetStage.name || targetStage.id);
            this.updateStageCounter();

            // Floor arrows do immediate transition (no portal animation)
            if (hotspot.portalEffectStyle === 'floor-arrow') {
              logger.debug('Floor arrow clicked, doing immediate transition');
              (async () => {
                try {
                  // Track stage navigation
                  this.analyticsService?.trackStageNavigation(
                    previousIndex,
                    targetIndex,
                    'jump',
                    this.experienceConfig!.stages.length
                  );

                  await this.loadCurrentStage();

                  // Apply target view direction if configured
                  if (hotspot.targetViewDirection) {
                    this.orientCameraToTarget(hotspot.targetViewDirection);
                  }
                } catch (error) {
                  logger.error('Floor arrow transition failed:', error);
                  this.currentStageIndex = previousIndex;
                  this.updateStageCounter();
                  if (this.experienceConfig) {
                    const prevStage = this.experienceConfig.stages[previousIndex];
                    this.updateSceneNameLabel(prevStage.name || prevStage.id);
                  }
                } finally {
                  this.isLoading = false;
                  this.setNavigationEnabled(true);
                }
              })();
            } else {
              // Start portal expansion transition for regular portals
              const portalPreviewManager = this.sceneManager.getPortalPreviewManager();
              const targetStageId = portalPreviewManager.startTransition(hotspot.id);

              // Setup callback for when transition completes
              if (targetStageId) {
                portalPreviewManager.onTransitionComplete = async () => {
                  try {
                    // Transition animation finished, now actually change the stage
                    logger.debug('Portal expansion complete! Transferring texture to main dome');

                    // Transfer texture from portal preview to avoid reloading
                    const transferredTexture = portalPreviewManager.transferTexture(hotspot.id);

                    // Track stage navigation
                    this.analyticsService?.trackStageNavigation(
                      previousIndex,
                      targetIndex,
                      'jump', // Use 'jump' for hotspot navigation
                      this.experienceConfig!.stages.length
                    );

                    // Load new stage FIRST (swaps main dome texture)
                    await this.loadCurrentStage(transferredTexture);

                    // No need to dispose portal previews - they get reinitialized for the new stage
                  } catch (error) {
                    logger.error('Stage transition failed:', error);
                    // Revert to previous stage on error
                    this.currentStageIndex = previousIndex;
                    this.updateStageCounter();
                    if (this.experienceConfig) {
                      const prevStage = this.experienceConfig.stages[previousIndex];
                      this.updateSceneNameLabel(prevStage.name || prevStage.id);
                    }
                  } finally {
                    // Always unlock navigation, even if error occurred
                    this.isLoading = false;
                    // Re-enable navigation and remove loader
                    this.setNavigationEnabled(true);
                  }
                };
              } else {
                // No preview dome found, fall back to immediate transition (need async)
                logger.warn('No preview dome, doing immediate transition');
                (async () => {
                  try {
                    await this.loadCurrentStage();
                  } catch (error) {
                    logger.error('Immediate stage transition failed:', error);
                    // Revert to previous stage on error
                    this.currentStageIndex = previousIndex;
                    this.updateStageCounter();
                    if (this.experienceConfig) {
                      const prevStage = this.experienceConfig.stages[previousIndex];
                      this.updateSceneNameLabel(prevStage.name || prevStage.id);
                    }
                  } finally {
                    // Always unlock navigation and remove loader
                    this.isLoading = false;
                    this.setNavigationEnabled(true);
                  }
                })();
              }
            }
          } else {
            logger.error(`Target stage not found: ${hotspot.targetStageId}`);
            // CRITICAL FIX: Clean up navigation lock if stage not found
            this.isLoading = false;
            this.setNavigationEnabled(true);
          }
        }
      }

      // Show info panel if hotspot has info content
      if (hotspot.type === 'info' || hotspot.type === 'both') {
        this.hotspotInfoPanel.show(hotspot, this.isInVR());
      }

      // Note: Analytics tracking for hotspot clicks could be added here
      // when a specific tracking method is available in AnalyticsService
    };

    // Handle lead capture form display
    hotspotComponent.onShowLeadForm = (hotspot: HotspotConfig) => {
      logger.debug('Show lead capture form:', hotspot.id);

      if (this.isInVR()) {
        // Show VR form panel
        if (this.leadCaptureFormVR) {
          const stageId = this.getCurrentStageId();
          this.leadCaptureFormVR.show(hotspot, stageId);
        }
      } else {
        // Show HTML form modal
        if (this.leadCaptureForm) {
          const stageId = this.getCurrentStageId();
          this.leadCaptureForm.show(hotspot, stageId);
        }
      }
    };

    // Handle hotspot hover (trigger portal preview for navigation hotspots)
    hotspotComponent.onHotspotHover = (hotspot: HotspotConfig) => {
      // Ignore hover during transitions to prevent visual glitches
      if (this.isLoading) return;

      logger.debug('Hotspot hovered:', hotspot.id);

      // Change cursor to pointer when hovering over interactive hotspots
      const canvas = this.scene.getEngine().getRenderingCanvas();
      if (canvas) {
        canvas.classList.add('cursor-pointer');
      }

      // Show portal preview or floor arrow hover effect for navigation hotspots
      if (hotspot.type === 'navigation' && hotspot.targetStageId) {
        if (hotspot.portalEffectStyle === 'floor-arrow') {
          // Floor arrow hover effect
          const floorArrowManager = this.sceneManager.getFloorArrowManager();
          floorArrowManager.showHover(hotspot.id);
        } else {
          // Portal preview effect
          const portalPreviewManager = this.sceneManager.getPortalPreviewManager();
          portalPreviewManager.showPreview(hotspot.id);
        }
      }
    };

    // Handle hotspot hover exit
    hotspotComponent.onHotspotHoverExit = (hotspot: HotspotConfig) => {
      // Ignore hover exit during transitions
      if (this.isLoading) return;

      logger.debug('Hotspot hover exit:', hotspot.id);

      // Restore grab cursor when leaving hotspots
      const canvas = this.scene.getEngine().getRenderingCanvas();
      if (canvas) {
        canvas.classList.remove('cursor-pointer');
      }

      // Hide portal preview or floor arrow hover effect for navigation hotspots
      if (hotspot.type === 'navigation' && hotspot.targetStageId) {
        if (hotspot.portalEffectStyle === 'floor-arrow') {
          // Floor arrow hover effect
          const floorArrowManager = this.sceneManager.getFloorArrowManager();
          floorArrowManager.hideHover(hotspot.id);
        } else {
          // Portal preview effect
          const portalPreviewManager = this.sceneManager.getPortalPreviewManager();
          portalPreviewManager.hidePreview(hotspot.id);
        }
      }
    };

    // Handle spatial audio play
    hotspotComponent.onAudioPlay = (hotspot: HotspotConfig) => {
      logger.debug('Spatial audio playing:', hotspot.id);
      this.showSpatialAudio(hotspot);
    };

    // Handle spatial audio stop
    hotspotComponent.onAudioStop = (hotspot: HotspotConfig) => {
      logger.debug('Spatial audio stopped:', hotspot.id);
      this.hideSpatialAudio();
    };

    // Handle floor arrow click - trigger navigation
    const floorArrowManager = this.sceneManager.getFloorArrowManager();
    floorArrowManager.onArrowClick = (hotspotId: string, targetStageId: string | undefined) => {
      logger.debug(`Arrow clicked: ${hotspotId}, target: ${targetStageId}`);

      // Hide tooltip immediately on click
      this.hideFloorArrowTooltip();

      // Can't navigate without a destination
      if (!targetStageId) {
        logger.warn(`Arrow ${hotspotId} has no destination set`);
        return;
      }

      // Prevent spam clicking during transition
      if (this.isLoading) {
        logger.debug('Already transitioning, ignoring click');
        return;
      }

      // Find target stage and navigate
      if (this.experienceConfig) {
        const targetIndex = this.experienceConfig.stages.findIndex(s => s.id === targetStageId);
        if (targetIndex >= 0) {
          this.isLoading = true;
          this.setNavigationEnabled(false);

          const previousIndex = this.currentStageIndex;
          this.currentStageIndex = targetIndex;
          const targetStage = this.experienceConfig.stages[targetIndex];
          this.updateSceneNameLabel(targetStage.name || targetStage.id);
          this.updateStageCounter();

          logger.debug(`Navigating to stage index ${targetIndex}`);

          // Find source hotspot config for targetViewDirection
          const sourceStage = this.experienceConfig.stages[previousIndex];
          const sourceHotspot = sourceStage?.hotspots?.find(h => h.id === hotspotId);

          (async () => {
            try {
              this.analyticsService?.trackStageNavigation(
                previousIndex,
                targetIndex,
                'jump',
                this.experienceConfig!.stages.length
              );
              await this.loadCurrentStage();

              // Apply target view direction if configured
              if (sourceHotspot?.targetViewDirection) {
                this.orientCameraToTarget(sourceHotspot.targetViewDirection);
              }
            } catch (error) {
              logger.error('Navigation failed:', error);
              this.currentStageIndex = previousIndex;
              this.updateStageCounter();
              if (this.experienceConfig) {
                this.updateSceneNameLabel(this.experienceConfig.stages[previousIndex].name || this.experienceConfig.stages[previousIndex].id);
              }
            } finally {
              this.isLoading = false;
              this.setNavigationEnabled(true);
            }
          })();
        }
      }
    };

    // Handle floor arrow hover - show tooltip with stage name
    floorArrowManager.onArrowHover = (hotspotId: string) => {
      if (!this.showFloorArrowTooltips || !this.experienceConfig) return;

      // Find the hotspot to get target stage ID
      const currentStage = this.experienceConfig.stages[this.currentStageIndex];
      const hotspot = currentStage?.hotspots?.find(h => h.id === hotspotId);
      const targetStageId = hotspot?.targetSceneId || hotspot?.targetStageId;

      if (targetStageId) {
        this.showFloorArrowTooltip(targetStageId);
      }
    };

    // Handle floor arrow hover exit - hide tooltip
    floorArrowManager.onArrowHoverExit = (_hotspotId: string) => {
      this.hideFloorArrowTooltip();
    };

    // Track mouse position for tooltip (use document listener for smoother tracking)
    document.addEventListener('mousemove', (e) => {
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
      this.updateFloorArrowTooltipPosition();
    });
  }

  // Mouse position tracking for tooltips
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;

  /**
   * Load experience configuration from JSON
   */
  async loadConfig(configUrl: string = '/config.json'): Promise<void> {
    try {
      const response = await fetch(configUrl);
      this.experienceConfig = await response.json();
      logger.info('Experience config loaded:', this.experienceConfig);

      // Set all stages in scene manager for portal preview system
      if (this.experienceConfig && this.experienceConfig.stages) {
        this.sceneManager.setAllStages(this.experienceConfig.stages);
      }

      // Apply global portal settings to PortalPreviewManager (for UI arrow transitions)
      this.applyPortalSettings();

      // Apply floor arrow tooltip setting (default: false)
      this.showFloorArrowTooltips = this.experienceConfig?.showFloorArrowTooltips ?? false;

      // Initialize navigation if configured
      await this.initializeNavigation();
    } catch (error) {
      logger.error('Failed to load config:', error);
      this.experienceConfig = {
        experience: {
          title: 'XRGallery',
          description: 'Immersive experience'
        },
        stages: []
      };
    }
  }

  /**
   * Load configuration from an object (for Firestore sequences)
   */
  loadConfigFromObject(config: ExperienceConfig): void {
    this.experienceConfig = config;
    logger.info('Experience config loaded from object:', this.experienceConfig);

    // Set all stages in scene manager for portal preview system
    if (this.experienceConfig && this.experienceConfig.stages) {
      this.sceneManager.setAllStages(this.experienceConfig.stages);
    }

    // Apply global portal settings to PortalPreviewManager (for UI arrow transitions)
    this.applyPortalSettings();

    // Apply floor arrow tooltip setting (default: false)
    this.showFloorArrowTooltips = this.experienceConfig?.showFloorArrowTooltips ?? false;

    // Initialize navigation if configured
    this.initializeNavigation();
  }

  /**
   * Apply global portal settings from experienceConfig to PortalPreviewManager
   * These settings affect UI arrow transitions (center portal transitions)
   */
  private applyPortalSettings(): void {
    if (!this.experienceConfig?.portalSettings) return;

    const portalPreviewManager = this.sceneManager.getPortalPreviewManager();
    const settings = this.experienceConfig.portalSettings;

    // Apply glow color (default: cyan)
    if (settings.glowColor) {
      portalPreviewManager.setPortalGlowColor(settings.glowColor);
      logger.debug(`Applied portal glow color: ${settings.glowColor}`);
    }

    // Apply edge opacity (0 = transparent, 1 = fully visible)
    if (settings.edgeOpacity !== undefined) {
      portalPreviewManager.setPortalEdgeOpacity(settings.edgeOpacity);
      logger.debug(`Applied portal edge opacity: ${settings.edgeOpacity}`);
    }

    // Apply effect style (clean, enhanced, minimal, alien)
    if (settings.effectStyle) {
      portalPreviewManager.setPortalEffectStyle(settings.effectStyle);
      logger.debug(`Applied portal effect style: ${settings.effectStyle}`);
    }
  }

  /**
   * Enable or disable floor arrow tooltips at runtime
   */
  setShowFloorArrowTooltips(show: boolean): void {
    this.showFloorArrowTooltips = show;
    logger.debug(`Floor arrow tooltips ${show ? 'enabled' : 'disabled'}`);
  }

  /**
   * Set whether to disable portal transition animations between stages
   * When enabled, stage changes happen instantly without the expanding portal effect
   */
  setDisableTransitions(disabled: boolean): void {
    this.disableTransitions = disabled;
    logger.debug(`Transitions ${disabled ? 'disabled' : 'enabled'}`);
  }

  /**
   * Set whether to disable gaze navigation (flashlight/xray portal mode)
   * When enabled, hides the flashlight button and prevents gaze navigation activation
   */
  setDisableGazeNavigation(disabled: boolean): void {
    this.disableGazeNavigation = disabled;

    // Hide/show the gaze mode button
    if (this.gazeModeBtn) {
      this.gazeModeBtn.style.display = disabled ? 'none' : '';
    }

    // If gaze navigation is being disabled while active, turn it off
    if (disabled && this.isFreeLookPortalMode) {
      this.toggleFreeLookPortalMode();
    }

    logger.debug(`Gaze navigation ${disabled ? 'disabled' : 'enabled'}`);
  }

  /**
   * Initialize the navigation manager with floor plan or map overlay
   */
  private async initializeNavigation(): Promise<void> {
    if (!this.experienceConfig) return;

    // Dispose existing navigation
    if (this.navigationManager) {
      this.navigationManager.dispose();
    }

    // Create navigation container if it doesn't exist
    let navContainer = document.getElementById('navigation-container');
    if (!navContainer) {
      navContainer = document.createElement('div');
      navContainer.id = 'navigation-container';
      document.body.appendChild(navContainer);
    }

    // Create navigation manager
    this.navigationManager = new NavigationManager(navContainer, {
      onNavigateToStage: (stageId) => this.navigateToStageById(stageId),
    });

    // Initialize with config
    await this.navigationManager.initialize(this.experienceConfig);

    // Set initial stage
    if (this.experienceConfig.stages.length > 0) {
      this.navigationManager.updateCurrentStageByIndex(this.currentStageIndex);
    }

    // Listen for minimap clicks to open stage selector with appropriate tab
    // Remove previous handler if it exists (prevents duplicate listeners)
    if (this.minimapClickHandler) {
      document.removeEventListener('navigation:minimapClick', this.minimapClickHandler);
    }

    this.minimapClickHandler = ((e: Event) => {
      const navType = (e as CustomEvent).detail?.navType as 'map' | 'floorplan' | 'none';
      if (navType === 'map') {
        this.openStageSelector('map');
      } else if (navType === 'floorplan') {
        this.openStageSelector('floorplan');
      } else {
        this.openStageSelector('stages');
      }
    });

    document.addEventListener('navigation:minimapClick', this.minimapClickHandler);
  }

  /**
   * Navigate to a stage by its ID
   */
  public navigateToStageById(stageId: string): void {
    if (!this.experienceConfig) return;

    const stageIndex = this.experienceConfig.stages.findIndex(s => s.id === stageId);
    if (stageIndex >= 0) {
      this.jumpToStage(stageIndex);
    } else {
      logger.warn(`Stage not found: ${stageId}`);
    }
  }

  /**
   * Get current experience config
   */
  getExperienceConfig(): ExperienceConfig | null {
    return this.experienceConfig;
  }

  /**
   * Initialize with already loaded config (skip loadConfig step)
   */
  async initializeWithLoadedConfig(progressOffset: number = 0, progressScale: number = 1): Promise<void> {
    this.updateButtonVisibility();

    if (this.experienceConfig && this.experienceConfig.stages.length > 0) {
      await this.loadCurrentStage(undefined, progressOffset, progressScale);
    } else {
      logger.warn('No stages configured in loaded config');
    }
  }

  /**
   * Create liquid glass UI at bottom center using HTML overlay
   */
  createUI(): void {
    // Get HTML elements
    this.navControls = document.getElementById('navControls');
    this.prevButton = document.getElementById('prevBtn') as HTMLButtonElement;
    this.nextButton = document.getElementById('nextBtn') as HTMLButtonElement;
    this.sceneNameLabel = document.getElementById('sceneNameText');
    this.progressBarContainer = document.getElementById('progressBarContainer');
    this.progressBarFill = document.getElementById('progressBarFill');
    // this.loadingScreen = document.getElementById('loading'); // Handled by LoadingUI
    this.stageCounterInline = document.getElementById('stageCounterInline');
    this.stageSelector = document.getElementById('stageSelector');
    this.stageList = document.getElementById('stageList');
    this.closeSelectorBtn = document.getElementById('closeSelectorBtn') as HTMLButtonElement;
    this.stageSelectorTabs = document.getElementById('stageSelectorTabs');
    this.stageSelectorMapContainer = document.getElementById('stageSelectorMapContainer');
    this.stageSelectorFloorplanContainer = document.getElementById('stageSelectorFloorplanContainer');
    this.controlButtonsContainer = document.querySelector('.control-buttons-container');
    this.vrControlsToggle = document.getElementById('vrControlsToggle');
    this.vrControlsInfo = document.getElementById('vrControlsInfo');
    this.fullscreenBtn = document.getElementById('fullscreenBtn') as HTMLButtonElement;
    this.gazeModeBtn = document.getElementById('gazeModeBtn') as HTMLButtonElement;
    this.enterVRButton = document.getElementById('enterVR') as HTMLButtonElement;
    this.playPauseBtn = document.getElementById('playPauseBtn') as HTMLButtonElement;
    this.playPauseIcon = document.getElementById('playPauseIcon');
    this.videoControls = document.getElementById('videoControls');
    this.audioControls = document.getElementById('audioControls');
    this.audioMuteBtn = document.getElementById('audioMuteBtn') as HTMLButtonElement;
    this.audioVolumeSlider = document.getElementById('audioVolumeSlider') as HTMLInputElement;
    this.audioTitlesDisplay = document.getElementById('audioTitlesDisplay');
    this.backgroundAudioTitle = document.getElementById('backgroundAudioTitle');
    this.spatialAudioTitle = document.getElementById('spatialAudioTitle');
    this.videoAudioTitle = document.getElementById('videoAudioTitle');
    this.tapToHearAudioOverlay = document.getElementById('tapToHearAudio');
    this.dragToLookHelper = document.getElementById('dragToLookHelper');

    // Setup helper overlays
    this.setupTapToHearAudioOverlay();
    this.setupDragToLookHelper();

    // Create floor arrow tooltip (hidden by default)
    this.createFloorArrowTooltip();

    // Initial layout update
    this.updateLayout();

    // Setup event listeners (storing handlers for proper cleanup)
    if (this.prevButton) {
      this.prevButtonHandler = () => this.previousStage();
      this.prevButton.addEventListener('click', this.prevButtonHandler);
    }

    if (this.nextButton) {
      this.nextButtonHandler = () => this.nextStage();
      this.nextButton.addEventListener('click', this.nextButtonHandler);
    }

    // Keyboard shortcuts for navigation
    this.setupKeyboardShortcuts();

    // Play/Pause button
    if (this.playPauseBtn) {
      this.playPauseBtn.addEventListener('click', () => this.toggleVideoPlayback());
    }

    // Audio controls
    if (this.audioMuteBtn) {
      this.audioMuteBtn.addEventListener('click', () => this.toggleAudioMute());
    }

    if (this.audioVolumeSlider) {
      this.audioVolumeSlider.addEventListener('input', () => this.updateAudioVolume());
    }

    // Progress bar scrubbing (click to seek)
    if (this.progressBarContainer) {
      this.progressBarContainer.style.cursor = 'pointer';
      this.progressBarContainer.addEventListener('click', (e) => this.handleProgressBarClick(e));
    }

    // Scene name click to open selector
    const sceneNameEl = document.getElementById('sceneName');
    if (sceneNameEl) {
      sceneNameEl.addEventListener('click', () => {
        logger.debug('Scene name clicked');
        this.openStageSelector();
      });
      // Keyboard accessibility - allow Enter/Space to activate
      sceneNameEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.openStageSelector();
        }
      });
    }

    // Stage selector close button
    if (this.closeSelectorBtn) {
      this.closeSelectorBtn.addEventListener('click', () => this.closeStageSelector());
    }

    // Close on background click
    if (this.stageSelector) {
      this.stageSelector.addEventListener('click', (e) => {
        if (e.target === this.stageSelector) {
          this.closeStageSelector();
        }
      });
    }

    // Tab switching in stage selector
    if (this.stageSelectorTabs) {
      this.stageSelectorTabs.addEventListener('click', (e) => {
        const tab = (e.target as HTMLElement).closest('.stage-selector-tab');
        if (tab) {
          const tabId = tab.getAttribute('data-tab') as 'stages' | 'map' | 'floorplan';
          if (tabId) {
            this.switchStageSelectorTab(tabId);
          }
        }
      });
    }

    // VR controls toggle
    if (this.vrControlsToggle) {
      this.vrControlsToggle.addEventListener('click', () => {
        this.toggleVrControlsInfo();
      });
    }

    // Fullscreen button
    if (this.fullscreenBtn) {
      this.fullscreenBtn.addEventListener('click', () => {
        this.toggleFullscreen();
      });
    }

    // Gaze mode button (portal look mode)
    if (this.gazeModeBtn) {
      this.gazeModeBtn.addEventListener('click', () => {
        this.toggleFreeLookPortalMode();
      });
    }

    // Listen for fullscreen changes to update button icon
    document.addEventListener('fullscreenchange', () => {
      this.updateFullscreenButton();
    });
    document.addEventListener('webkitfullscreenchange', () => {
      this.updateFullscreenButton();
    });

    // Keyboard shortcuts - ESC to close, M for menu, F for fullscreen, G for gaze
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isStageSelectorOpen) {
        this.closeStageSelector();
      } else if (e.key === 'Escape' && this.isVrControlsInfoOpen) {
        this.closeVrControlsInfo();
      } else if (e.key === 'Escape' && this.isFreeLookPortalMode) {
        this.toggleFreeLookPortalMode();
      } else if (e.key === 'm' || e.key === 'M') {
        this.toggleStageSelector();
      } else if (e.key === 'f' || e.key === 'F') {
        this.toggleFullscreen();
      } else if (e.key === 'g' || e.key === 'G') {
        this.toggleFreeLookPortalMode();
      }
    });

    // Scroll wheel - control gaze portal transition progress
    window.addEventListener('wheel', (e) => {
      if (!this.isFreeLookPortalMode) return;

      const portalPreviewManager = this.sceneManager.getPortalPreviewManager();

      // Much smaller delta for smooth control - normalize scroll distance
      const normalizedDelta = e.deltaY / 1000; // Typically deltaY is 100-120 per notch
      const delta = Math.max(-0.01, Math.min(0.01, normalizedDelta)); // Clamp to ±0.01

      portalPreviewManager.adjustGazePortalProgress(delta);

      // Block all default scroll behavior
      e.preventDefault();
      e.stopPropagation();
      return false;
    }, { passive: false, capture: true }); // Use capture phase to intercept early

    // Create 3D VR GUI
    this.createVRStageSelector();
    this.createVRLoadingIndicator();
    this.createHandTrackingNavPanel();
  }

  /**
   * Create 3D GUI stage selector for VR (camera-anchored)
   */
  private createVRStageSelector(): void {
    // Get VR GUI scale factor based on device
    const vrScale = DeviceDetector.getVRGUIScale();
    logger.debug(`Creating VR GUI with scale factor: ${vrScale} for device: ${DeviceDetector.getDeviceType()}`);

    // Create a plane for the GUI (no parent, we'll position it directly)
    // Aspect ratio: 3:4 (slightly portrait) - 1.5m wide x 2m tall (scaled for mobile)
    const baseWidth = 1.5;
    const baseHeight = 2.0;

    this.vrGuiPlane = MeshBuilder.CreatePlane('vrGuiPlane', {
      width: baseWidth * vrScale,
      height: baseHeight * vrScale,
      sideOrientation: Mesh.DOUBLESIDE,
    }, this.scene);

    // Initial position (will be updated each frame)
    this.vrGuiPlane.position = new Vector3(0, 1.6, -2);
    this.vrGuiPlane.isVisible = false; // Hidden by default
    this.vrGuiPlane.renderingGroupId = 1; // Render on top of other objects
    this.vrGuiPlane.isPickable = true; // Make it pickable for VR controller interaction
    // No billboardMode — manual Y-rotation in onBeforeRenderObservable handles facing

    // Create AdvancedDynamicTexture for the plane - matching aspect ratio 1536x2048 (3:4)
    this.vrGuiTexture = AdvancedDynamicTexture.CreateForMesh(
      this.vrGuiPlane,
      1536,
      2048,
      false
    );

    // Create main panel with liquid glass styling (matching Enter VR button)
    this.vrGuiPanel = new Rectangle('vrGuiPanel');
    this.vrGuiPanel.width = '1400px';
    this.vrGuiPanel.height = '1950px';
    this.vrGuiPanel.thickness = 2;
    this.vrGuiPanel.cornerRadius = 40;
    // Liquid glass effect - semi-transparent white with blur simulation
    this.vrGuiPanel.background = 'rgba(255, 255, 255, 0.12)';
    this.vrGuiPanel.color = 'rgba(255, 255, 255, 0.25)';
    this.vrGuiPanel.shadowBlur = 40;
    this.vrGuiPanel.shadowColor = 'rgba(0, 0, 0, 0.25)';
    this.vrGuiPanel.shadowOffsetY = 12;

    this.vrGuiTexture.addControl(this.vrGuiPanel);

    // Create header
    const header = new Rectangle('header');
    header.width = '1400px';
    header.height = '120px';
    header.thickness = 0;
    header.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.vrGuiPanel.addControl(header);

    const title = new TextBlock('title', 'Select Stage');
    title.color = 'white';
    title.fontSize = 48;
    title.fontWeight = '600';
    title.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    title.paddingLeft = '40px';
    header.addControl(title);

    // Create close button with liquid glass styling
    const closeBtn = Button.CreateSimpleButton('closeBtn', '✕');
    closeBtn.width = '80px';
    closeBtn.height = '80px';
    closeBtn.color = 'white';
    closeBtn.fontSize = 48;
    closeBtn.thickness = 2;
    closeBtn.cornerRadius = 40;
    closeBtn.background = 'rgba(255, 255, 255, 0.15)';
    closeBtn.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    closeBtn.paddingRight = '25px';
    closeBtn.shadowBlur = 8;
    closeBtn.shadowColor = 'rgba(0, 0, 0, 0.2)';
    closeBtn.onPointerEnterObservable.add(() => {
      closeBtn.background = 'rgba(255, 255, 255, 0.25)';
    });
    closeBtn.onPointerOutObservable.add(() => {
      closeBtn.background = 'rgba(255, 255, 255, 0.15)';
    });
    closeBtn.onPointerClickObservable.add(() => {
      this.closeVRStageSelector();
    });
    header.addControl(closeBtn);

    // Create scrollable stage list
    this.vrStageList = new StackPanel('vrStageList');
    this.vrStageList.width = '1300px';
    this.vrStageList.height = '1750px';
    this.vrStageList.top = '140px';
    this.vrStageList.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.vrStageList.spacing = 12;
    this.vrGuiPanel.addControl(this.vrStageList);

    // Update GUI plane to follow camera each frame
    let frameCount = 0;
    this.scene.onBeforeRenderObservable.add(() => {
      if (this.vrGuiPlane && this.scene.activeCamera && this.vrGuiPlane.isVisible) {
        // Position plane directly in front of camera
        const camera = this.scene.activeCamera;
        const forward = camera.getForwardRay().direction;
        const cameraPos = camera.position.clone();

        // Place GUI 2 meters in front of camera
        const targetPosition = cameraPos.add(forward.scale(2));
        this.vrGuiPlane.position.copyFrom(targetPosition);

        // Y-axis only rotation to face camera (keeps panel upright in VR)
        const direction = cameraPos.subtract(this.vrGuiPlane.position);
        const angle = Math.atan2(direction.x, direction.z);
        this.vrGuiPlane.rotation.y = angle + Math.PI; // Face camera
        this.vrGuiPlane.rotation.x = 0; // Keep upright
        this.vrGuiPlane.rotation.z = 0; // No roll

        // Debug logging (only log every 60 frames to avoid spam)
        if (frameCount % 60 === 0) {
          logger.debug('VR GUI Camera pos:', cameraPos);
          logger.debug('VR GUI Forward:', forward);
          logger.debug('VR GUI Plane pos:', this.vrGuiPlane.position);
        }
        frameCount++;
      }
    });

    logger.debug('VR Stage Selector created');
  }

  /**
   * Create VR loading indicator (camera-anchored)
   */
  private createVRLoadingIndicator(): void {
    // Get VR GUI scale factor based on device
    const vrScale = DeviceDetector.getVRGUIScale();

    // Create a smaller plane for the loading indicator
    const width = 0.8 * vrScale;
    const height = 0.4 * vrScale;

    this.vrLoadingPlane = MeshBuilder.CreatePlane('vrLoadingPlane', {
      width,
      height,
      sideOrientation: Mesh.DOUBLESIDE,
    }, this.scene);

    // Start hidden
    this.vrLoadingPlane.isVisible = false;

    // Create advanced texture for loading UI
    this.vrLoadingTexture = AdvancedDynamicTexture.CreateForMesh(
      this.vrLoadingPlane,
      1024,
      512,
      false
    );

    // Create container panel
    this.vrLoadingPanel = new Rectangle('vrLoadingPanel');
    this.vrLoadingPanel.width = '100%';
    this.vrLoadingPanel.height = '100%';
    this.vrLoadingPanel.thickness = 0;
    this.vrLoadingPanel.background = 'transparent';
    this.vrLoadingPanel.cornerRadius = 30;
    this.vrLoadingTexture.addControl(this.vrLoadingPanel);

    // Create loading spinner (animated dots)
    this.vrLoadingSpinner = new TextBlock('vrLoadingSpinner');
    this.vrLoadingSpinner.text = '●●●';
    this.vrLoadingSpinner.color = 'white';
    this.vrLoadingSpinner.fontSize = 60;
    this.vrLoadingSpinner.fontWeight = 'bold';
    this.vrLoadingSpinner.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    this.vrLoadingSpinner.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    this.vrLoadingSpinner.top = '-40px';
    this.vrLoadingPanel.addControl(this.vrLoadingSpinner);

    // Create loading text
    this.vrLoadingText = new TextBlock('vrLoadingText');
    this.vrLoadingText.text = 'Loading...';
    this.vrLoadingText.color = 'rgba(255, 255, 255, 0.9)';
    this.vrLoadingText.fontSize = 36;
    this.vrLoadingText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    this.vrLoadingText.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    this.vrLoadingText.top = '40px';
    this.vrLoadingPanel.addControl(this.vrLoadingText);

    // Position loading indicator in front of camera (same logic as VR menu)
    this.scene.onBeforeRenderObservable.add(() => {
      if (this.vrLoadingPlane && this.scene.activeCamera && this.vrLoadingPlane.isVisible) {
        // Position plane directly in front of camera
        const camera = this.scene.activeCamera;
        const forward = camera.getForwardRay().direction;
        const cameraPos = camera.position.clone();

        // Place loading indicator 2 meters in front of camera (same as VR menu)
        const targetPosition = cameraPos.add(forward.scale(2));
        this.vrLoadingPlane.position.copyFrom(targetPosition);

        // Y-axis only rotation to face camera (keeps panel upright in VR)
        const direction = cameraPos.subtract(this.vrLoadingPlane.position);
        const angle = Math.atan2(direction.x, direction.z);
        this.vrLoadingPlane.rotation.y = angle + Math.PI; // Face camera
        this.vrLoadingPlane.rotation.x = 0; // Keep upright
        this.vrLoadingPlane.rotation.z = 0; // No roll

        // Animate spinner
        this.animateVRLoadingSpinner();
      }
    });

    logger.debug('VR Loading Indicator created');
  }

  /**
   * Animate the VR loading spinner
   */
  private animateVRLoadingSpinner(): void {
    if (!this.vrLoadingSpinner) return;

    // Update every 10 frames (~6fps for smooth but not too fast animation)
    this.vrLoadingAnimationFrame++;
    if (this.vrLoadingAnimationFrame % 10 !== 0) return;

    // Cycle through different spinner states
    const spinnerStates = ['●○○', '○●○', '○○●', '○●○'];
    const stateIndex = Math.floor(this.vrLoadingAnimationFrame / 10) % spinnerStates.length;
    this.vrLoadingSpinner.text = spinnerStates[stateIndex];
  }

  /**
   * Show VR loading indicator
   */
  private showVRLoading(): void {
    if (this.vrLoadingPlane) {
      logger.debug('Showing VR loading indicator');
      this.vrLoadingPlane.isVisible = true;
      this.vrLoadingAnimationFrame = 0;
    }
  }

  /**
   * Hide VR loading indicator
   */
  private hideVRLoading(): void {
    if (this.vrLoadingPlane) {
      logger.debug('Hiding VR loading indicator');
      this.vrLoadingPlane.isVisible = false;
    }
  }

  /**
   * Create floating navigation bar for hand tracking mode (AVP, Quest hand tracking)
   * Shows prev/menu/next buttons that can be targeted with eye tracking + pinch
   */
  private createHandTrackingNavPanel(): void {
    const vrScale = DeviceDetector.getVRGUIScale();

    // Horizontal bar: 1.4m wide x 0.18m tall — wide enough for 4 elements
    const barWidth = 1.4 * vrScale;
    const barHeight = 0.18 * vrScale;

    this.handNavPlane = MeshBuilder.CreatePlane('handNavPlane', {
      width: barWidth,
      height: barHeight,
      sideOrientation: Mesh.DOUBLESIDE,
    }, this.scene);

    this.handNavPlane.position = new Vector3(0, 1.2, -2);
    this.handNavPlane.isVisible = false;
    this.handNavPlane.renderingGroupId = 1;
    this.handNavPlane.isPickable = true;

    // High-res texture: 1400x180 for crisp rendering on AVP
    this.handNavTexture = AdvancedDynamicTexture.CreateForMesh(
      this.handNavPlane,
      1400,
      180,
      false
    );

    // Outer container — liquid glass pill shape
    const container = new Rectangle('handNavContainer');
    container.width = '1380px';
    container.height = '170px';
    container.thickness = 1;
    container.cornerRadius = 85;
    container.background = 'rgba(255, 255, 255, 0.10)';
    container.color = 'rgba(255, 255, 255, 0.20)';
    container.shadowBlur = 30;
    container.shadowColor = 'rgba(0, 0, 0, 0.30)';
    container.shadowOffsetY = 6;
    this.handNavTexture.addControl(container);

    // Helper to create a liquid glass button
    const makeButton = (name: string, label: string, fontSize: number, onClick: () => void) => {
      const btn = Button.CreateSimpleButton(name, label);
      btn.width = '150px';
      btn.height = '130px';
      btn.color = 'white';
      btn.fontSize = fontSize;
      btn.thickness = 1;
      btn.cornerRadius = 65;
      btn.background = 'rgba(255, 255, 255, 0.08)';
      btn.shadowBlur = 8;
      btn.shadowColor = 'rgba(0, 0, 0, 0.15)';
      btn.onPointerEnterObservable.add(() => {
        btn.background = 'rgba(255, 255, 255, 0.28)';
        btn.thickness = 2;
        btn.color = 'rgba(255, 255, 255, 0.40)';
      });
      btn.onPointerOutObservable.add(() => {
        btn.background = 'rgba(255, 255, 255, 0.08)';
        btn.thickness = 1;
        btn.color = 'white';
      });
      btn.onPointerClickObservable.add(onClick);
      return btn;
    };

    // Layout: ◀ | scene label | ▶ | ☰
    // Previous button — far left
    const prevBtn = makeButton('handNavPrev', '◀', 44, () => this.previousStage());
    prevBtn.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    prevBtn.left = '15px';
    container.addControl(prevBtn);

    // Scene label — center, sized to avoid overlapping buttons
    const sceneLabel = new TextBlock('handNavSceneLabel');
    sceneLabel.text = '';
    sceneLabel.color = 'rgba(255, 255, 255, 0.9)';
    sceneLabel.fontSize = 34;
    sceneLabel.fontWeight = '500';
    sceneLabel.width = '700px';
    sceneLabel.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    sceneLabel.isHitTestVisible = false; // Don't intercept pointer events meant for buttons
    container.addControl(sceneLabel);

    // Next button — right of center
    const nextBtn = makeButton('handNavNext', '▶', 44, () => this.nextStage());
    nextBtn.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    nextBtn.left = '-175px';
    container.addControl(nextBtn);

    // Menu button — far right
    const menuBtn = makeButton('handNavMenu', '☰', 48, () => this.toggleStageSelector());
    menuBtn.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    menuBtn.left = '-15px';
    container.addControl(menuBtn);

    // Position the nav bar each frame: below center of view, follows gaze
    this.scene.onBeforeRenderObservable.add(() => {
      if (!this.handNavPlane || !this.scene.activeCamera || !this.handNavPlane.isVisible) return;

      const camera = this.scene.activeCamera;
      const forward = camera.getForwardRay().direction;
      const cameraPos = camera.position.clone();

      // Place 2m ahead, offset down so it sits at the lower portion of the FOV
      const downOffset = new Vector3(0, -0.55, 0);
      const targetPosition = cameraPos.add(forward.scale(2)).add(downOffset);
      this.handNavPlane.position.copyFrom(targetPosition);

      // Face camera (Y-axis rotation only to keep bar level)
      const direction = cameraPos.subtract(this.handNavPlane.position);
      const angle = Math.atan2(direction.x, direction.z);
      this.handNavPlane.rotation.y = angle + Math.PI;
      this.handNavPlane.rotation.x = 0;
      this.handNavPlane.rotation.z = 0;
    });

    // Tap on empty skybox to toggle hand nav visibility
    // Tap on empty space (no interactive mesh hit) to toggle hand nav
    this.scene.onPointerObservable.add((pointerInfo) => {
      if (!this.handTrackingActive) return;
      if (this.isStageSelectorOpen) return;

      const pickedMesh = pointerInfo.pickInfo?.pickedMesh;
      // No mesh picked = tapped empty sky (dome is not pickable)
      if (!pickedMesh) {
        this.setHandTrackingNavVisible(!this.handNavVisible);
      }
    }, PointerEventTypes.POINTERTAP);

    logger.debug('Hand tracking navigation panel created');
  }

  /**
   * Show/hide the hand tracking navigation panel
   */
  private setHandTrackingNavVisible(visible: boolean): void {
    if (!this.handNavPlane) return;
    if (this.handNavVisible === visible) return;

    this.handNavVisible = visible;
    this.handNavPlane.isVisible = visible;
    logger.info(`Hand tracking nav panel ${visible ? 'shown' : 'hidden'}`);

    // Update the scene label when showing
    if (visible) {
      this.updateHandNavLabel();
    }
  }

  /**
   * Update the hand nav scene label with current stage info
   */
  private updateHandNavLabel(): void {
    if (!this.handNavTexture || !this.experienceConfig) return;

    const label = this.handNavTexture.getControlByName('handNavSceneLabel') as TextBlock;
    if (!label) return;

    const stage = this.experienceConfig.stages[this.currentStageIndex];
    const total = this.experienceConfig.stages.length;
    label.text = `${this.currentStageIndex + 1}/${total}  ${stage?.name || stage?.id || ''}`;
  }

  /**
   * Build VR stage list
   */
  private buildVRStageList(): void {
    if (!this.vrStageList || !this.experienceConfig) return;

    // Clear existing items
    this.vrStageList.clearControls();

    // Create stage items
    this.experienceConfig.stages.forEach((stage, index) => {
      const stageItem = new Rectangle(`vrStage_${index}`);
      stageItem.width = '1300px';
      stageItem.height = '110px';
      stageItem.thickness = 2;
      stageItem.cornerRadius = 20;

      // Highlight current stage only (no selected state)
      const isCurrent = index === this.currentStageIndex;

      stageItem.background = isCurrent
        ? 'rgba(255, 255, 255, 0.25)'  // White for current stage
        : 'rgba(255, 255, 255, 0.08)'; // Dim for others
      stageItem.color = 'rgba(255, 255, 255, 0.3)';
      stageItem.isPointerBlocker = true;

      // Hover effect - track which item is being hovered
      stageItem.onPointerEnterObservable.add(() => {
        this.vrMenuHoveredIndex = index;
        stageItem.background = 'rgba(255, 255, 255, 0.35)'; // Brighter hover effect
      });
      stageItem.onPointerOutObservable.add(() => {
        if (this.vrMenuHoveredIndex === index) {
          this.vrMenuHoveredIndex = -1;
        }
        const isStillCurrent = index === this.currentStageIndex;
        stageItem.background = isStillCurrent
          ? 'rgba(255, 255, 255, 0.25)'
          : 'rgba(255, 255, 255, 0.08)';
      });

      // Click to select stage (skip if already on this stage)
      if (!isCurrent) {
        stageItem.onPointerClickObservable.add(() => {
          this.jumpToStage(index);
          this.closeVRStageSelector();
        });
      }

      // Stage number
      const stageNum = new TextBlock();
      stageNum.text = `Scene ${index + 1} of ${this.experienceConfig!.stages.length}`;
      stageNum.color = 'rgba(255, 255, 255, 0.7)';
      stageNum.fontSize = 28;
      stageNum.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      stageNum.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
      stageNum.paddingLeft = '30px';
      stageNum.paddingTop = '12px';
      stageItem.addControl(stageNum);

      // Stage name
      const stageName = new TextBlock();
      stageName.text = stage.name || stage.id;
      stageName.color = 'white';
      stageName.fontSize = 38;
      stageName.fontWeight = '500';
      stageName.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      stageName.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
      stageName.paddingLeft = '30px';
      stageName.paddingBottom = '12px';
      stageItem.addControl(stageName);

      // Current indicator
      if (isCurrent) {
        const indicator = new TextBlock();
        indicator.text = '▶';
        indicator.color = 'white';
        indicator.fontSize = 40;
        indicator.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        indicator.paddingRight = '30px';
        stageItem.addControl(indicator);
      }

      this.vrStageList!.addControl(stageItem);
    });
  }

  /**
   * Select the currently hovered menu item (or do nothing if no hover)
   */
  private selectHoveredMenuItem(): void {
    if (!this.isStageSelectorOpen) return;

    // If hovering over an item, select it (skip if already on this stage)
    if (this.vrMenuHoveredIndex >= 0 && this.vrMenuHoveredIndex !== this.currentStageIndex) {
      logger.debug(`Selecting hovered menu item: ${this.vrMenuHoveredIndex}`);
      this.jumpToStage(this.vrMenuHoveredIndex);
      this.closeVRStageSelector();
    } else {
      logger.debug('No menu item hovered, ignoring trigger');
    }
  }

  /**
   * Open VR stage selector
   */
  private openVRStageSelector(): void {
    if (!this.vrGuiPlane) {
      logger.error('VR GUI plane not found!');
      return;
    }

    logger.debug('Opening VR stage selector');

    // Track VR stage selector opened
    this.analyticsService?.trackStageSelectorOpened('VR', this.currentStageIndex);

    // Reset hover state
    this.vrMenuHoveredIndex = -1;
    this.buildVRStageList();
    this.vrGuiPlane.isVisible = true;
    this.isStageSelectorOpen = true;

    // Hide hand nav to avoid overlap
    this.setHandTrackingNavVisible(false);
  }

  /**
   * Close VR stage selector
   */
  private closeVRStageSelector(): void {
    if (!this.vrGuiPlane) return;

    logger.debug('Closing VR stage selector');

    // Track VR stage selector closed
    this.analyticsService?.trackStageSelectorClosed('VR', this.currentStageIndex);

    this.vrGuiPlane.isVisible = false;
    this.isStageSelectorOpen = false;

    // Restore hand nav if hand tracking is active
    if (this.handTrackingActive) {
      this.setHandTrackingNavVisible(true);
    }
  }

  /**
   * Toggle stage selector (auto-detects VR vs 2D)
   */
  private toggleStageSelector(): void {
    if (this.isStageSelectorOpen) {
      this.closeStageSelector();
      this.closeVRStageSelector();
    } else {
      // Check if in VR mode
      if (this.isInVR()) {
        this.openVRStageSelector();
      } else {
        this.openStageSelector();
      }
    }
  }

  /**
   * Check if currently in VR mode
   */
  private isInVR(): boolean {
    // Check if camera is in VR (WebXR camera)
    return this.scene.activeCamera?.getClassName() === 'WebXRCamera';
  }

  /**
   * Get current stage ID
   */
  private getCurrentStageId(): string {
    if (!this.experienceConfig || this.currentStageIndex < 0) {
      return '';
    }
    return this.experienceConfig.stages[this.currentStageIndex]?.id || '';
  }

  /**
   * Initialize lead capture forms
   * Called by main.ts after sequence is loaded
   */
  public initializeLeadCaptureForms(sequenceId: string, sessionId: string): void {
    this.leadFormSequenceId = sequenceId;
    this.leadFormSessionId = sessionId;

    // Create analytics callbacks that log form events with context
    const analyticsCallbacks = {
      onFormViewed: (seqId: string, hotspotId: string, formType: string) => {
        logger.debug(`Lead form viewed: sequence=${seqId}, hotspot=${hotspotId}, type=${formType}`);
      },
      onFormSubmitted: (seqId: string, hotspotId: string, formType: string) => {
        logger.info(`Lead form submitted: sequence=${seqId}, hotspot=${hotspotId}, type=${formType}`);
      },
      onFormAbandoned: (seqId: string, hotspotId: string, formType: string) => {
        logger.debug(`Lead form abandoned: sequence=${seqId}, hotspot=${hotspotId}, type=${formType}`);
      },
      onFormError: (seqId: string, hotspotId: string, error: string) => {
        logger.error(`Lead form error: sequence=${seqId}, hotspot=${hotspotId}, error=${error}`);
      }
    };

    // Create HTML form (desktop/mobile)
    this.leadCaptureForm = new LeadCaptureForm(
      this.scene,
      sequenceId,
      sessionId,
      analyticsCallbacks
    );

    // Create VR form
    this.leadCaptureFormVR = new LeadCaptureFormVR(
      this.scene,
      sequenceId,
      sessionId,
      analyticsCallbacks
    );

    logger.info(`Lead capture forms initialized for sequence: ${sequenceId}`);
  }

  /**
   * Open stage selector HUD (2D mode)
   * @param defaultTab Optional tab to open to (defaults to 'stages')
   */
  private openStageSelector(defaultTab?: 'stages' | 'map' | 'floorplan'): void {
    if (!this.stageSelector || !this.experienceConfig) return;

    logger.debug('Opening 2D stage selector');
    this.isStageSelectorOpen = true;
    this.stageSelector.classList.remove('hidden');

    // Track stage selector opened
    this.analyticsService?.trackStageSelectorOpened('2D', this.currentStageIndex);

    // Configure visible tabs based on navigation type
    this.configureStageSelectorTabs();

    // Build stage list
    this.buildStageList();

    // Switch to default tab if specified
    if (defaultTab) {
      this.switchStageSelectorTab(defaultTab);
    }
  }

  /**
   * Configure which tabs are visible based on navigation config
   */
  private configureStageSelectorTabs(): void {
    const navType = this.experienceConfig?.navigation?.type;

    const mapTab = this.stageSelectorTabs?.querySelector('[data-tab="map"]');
    const floorplanTab = this.stageSelectorTabs?.querySelector('[data-tab="floorplan"]');

    // Show/hide tabs based on navigation type
    if (mapTab) {
      mapTab.classList.toggle('hidden', navType !== 'map');
    }
    if (floorplanTab) {
      floorplanTab.classList.toggle('hidden', navType !== 'floorplan');
    }

    // If only stages tab is visible (no navigation), hide the tab bar entirely
    const hasNavigation = navType === 'map' || navType === 'floorplan';
    if (this.stageSelectorTabs) {
      this.stageSelectorTabs.style.display = hasNavigation ? 'flex' : 'none';
    }
  }

  /**
   * Close stage selector HUD (2D mode)
   */
  private closeStageSelector(): void {
    if (!this.stageSelector) return;

    logger.debug('Closing 2D stage selector');
    this.isStageSelectorOpen = false;
    this.stageSelector.classList.add('hidden');

    // Track stage selector closed
    this.analyticsService?.trackStageSelectorClosed('2D', this.currentStageIndex);
  }

  /**
   * Toggle VR controls info panel
   */
  private toggleVrControlsInfo(): void {
    if (this.isVrControlsInfoOpen) {
      this.closeVrControlsInfo();
    } else {
      this.openVrControlsInfo();
    }
  }

  /**
   * Open VR controls info panel
   */
  private openVrControlsInfo(): void {
    if (!this.vrControlsInfo) return;

    logger.debug('Opening VR controls info');
    this.isVrControlsInfoOpen = true;
    this.vrControlsInfo.classList.remove('hidden');
  }

  /**
   * Close VR controls info panel
   */
  private closeVrControlsInfo(): void {
    if (!this.vrControlsInfo) return;

    logger.debug('Closing VR controls info');
    this.isVrControlsInfoOpen = false;
    this.vrControlsInfo.classList.add('hidden');
  }

  /**
   * Toggle fullscreen mode
   */
  private toggleFullscreen(): void {
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      // Enter fullscreen
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      }
      logger.debug('Entering fullscreen');
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
      logger.debug('Exiting fullscreen');
    }
  }

  /**
   * Update fullscreen button icon based on current state
   */
  private updateFullscreenButton(): void {
    if (!this.fullscreenBtn) return;

    const isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);

    // Update button icon - ⛶ for enter fullscreen, ⛉ for exit fullscreen
    this.fullscreenBtn.textContent = isFullscreen ? '⛉' : '⛶';
    this.fullscreenBtn.title = isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen';

    // Hide Enter VR button when in fullscreen, show when not
    if (this.enterVRButton) {
      if (isFullscreen) {
        this.enterVRButton.style.display = 'none';
      } else {
        this.enterVRButton.style.display = '';
      }
      this.updateLayout();
    }
  }

  /**
   * Update layout based on UI state
   */
  public updateLayout(): void {
    if (!this.videoControls) return;

    // Check if Enter VR button is visible
    const isVrBtnVisible = this.enterVRButton &&
                           this.enterVRButton.style.display !== 'none' &&
                           this.enterVRButton.offsetParent !== null;

    // Adjust video controls position
    // If VR button is visible, move up to 165px, otherwise 125px
    this.videoControls.style.bottom = isVrBtnVisible ? '165px' : '125px';
  }

  /**
   * Toggle free-look portal mode (gaze navigation)
   */
  private toggleFreeLookPortalMode(): void {
    // Don't allow enabling gaze navigation if it's disabled for this sequence
    // (but always allow disabling it if somehow active)
    if (this.disableGazeNavigation && !this.isFreeLookPortalMode) {
      return;
    }

    this.isFreeLookPortalMode = !this.isFreeLookPortalMode;

    // Update button visual state
    this.updateGazeModeButton();

    const portalPreviewManager = this.sceneManager.getPortalPreviewManager();

    if (this.isFreeLookPortalMode) {
      logger.debug('Entering gaze navigation mode - Press G or scroll to exit');

      // Get next stage for preview
      if (!this.experienceConfig || this.experienceConfig.stages.length === 0) return;

      const nextIndex = (this.currentStageIndex + 1) % this.experienceConfig.stages.length;
      const nextStage = this.experienceConfig.stages[nextIndex];

      // Show gaze portal (drawn on top of regular portals - no need to hide them)
      portalPreviewManager.showGazePortal(nextStage);

    } else {
      logger.debug('Exiting gaze navigation mode');

      // Hide gaze portal
      portalPreviewManager.hideGazePortal();
    }
  }

  /**
   * Update gaze mode button visual state
   */
  private updateGazeModeButton(): void {
    if (!this.gazeModeBtn) return;

    const flashlightOff = this.gazeModeBtn.querySelector('#flashlightOff') as SVGElement;
    const flashlightOn = this.gazeModeBtn.querySelector('#flashlightOn') as SVGElement;

    if (this.isFreeLookPortalMode) {
      this.gazeModeBtn.classList.add('active');
      this.gazeModeBtn.title = 'Exit Gaze Navigation (G)';
      // Show "on" flashlight, hide "off"
      if (flashlightOff) flashlightOff.style.display = 'none';
      if (flashlightOn) flashlightOn.style.display = 'block';
    } else {
      this.gazeModeBtn.classList.remove('active');
      this.gazeModeBtn.title = 'Toggle Gaze Navigation (G)';
      // Show "off" flashlight, hide "on"
      if (flashlightOff) flashlightOff.style.display = 'block';
      if (flashlightOn) flashlightOn.style.display = 'none';
    }
  }

  /**
   * Build the stage list UI
   */
  private buildStageList(): void {
    if (!this.stageList || !this.experienceConfig) return;

    // Clear existing items
    this.stageList.innerHTML = '';

    // Create stage items
    this.experienceConfig.stages.forEach((stage, index) => {
      const stageItem = document.createElement('div');
      stageItem.className = 'stage-item';
      if (index === this.currentStageIndex) {
        stageItem.classList.add('active');
      }

      stageItem.innerHTML = `
        <div class="stage-item-content">
          <div class="stage-item-number">Scene ${index + 1} of ${this.experienceConfig!.stages.length}</div>
          <div class="stage-item-name">${stage.name || stage.id}</div>
        </div>
        <div class="stage-item-icon">${index === this.currentStageIndex ? '▶' : '○'}</div>
      `;

      if (index !== this.currentStageIndex) {
        stageItem.addEventListener('click', () => {
          this.jumpToStage(index);
        });
      }

      this.stageList!.appendChild(stageItem);
    });
  }

  /**
   * Switch the stage selector tab
   */
  private switchStageSelectorTab(tabId: 'stages' | 'map' | 'floorplan'): void {
    // Update tab buttons
    const tabs = this.stageSelectorTabs?.querySelectorAll('.stage-selector-tab');
    tabs?.forEach(tab => {
      tab.classList.toggle('active', tab.getAttribute('data-tab') === tabId);
    });

    // Update tab content visibility
    const contents = this.stageSelector?.querySelectorAll('.stage-selector-tab-content');
    contents?.forEach(content => {
      content.classList.toggle('active', content.getAttribute('data-tab-content') === tabId);
    });

    // Initialize map or floorplan if switching to those tabs
    if (tabId === 'map') {
      this.initializeStageSelectorMap();
    } else if (tabId === 'floorplan') {
      this.initializeStageSelectorFloorplan();
    }
  }

  /**
   * Initialize map inside stage selector
   */
  private async initializeStageSelectorMap(): Promise<void> {
    if (!this.stageSelectorMapContainer || !this.experienceConfig?.navigation) return;

    const navConfig = this.experienceConfig.navigation;
    if (navConfig.type !== 'map') {
      this.renderEmptyState(this.stageSelectorMapContainer, 'map');
      return;
    }

    // Check if map is already initialized
    if (this.stageSelectorMap) {
      this.stageSelectorMap.invalidateSize();
      return;
    }

    // Load Leaflet if needed
    const L = await this.loadLeaflet();
    if (!L) {
      this.renderEmptyState(this.stageSelectorMapContainer, 'map', 'Unable to load map. Please check your connection.');
      return;
    }

    // Create map
    this.stageSelectorMap = L.map(this.stageSelectorMapContainer, {
      zoomControl: true,
      attributionControl: false,
    });

    // Get configured map style (default to streets)
    const mapStyle = navConfig.map?.style || 'streets';
    const tileConfig = getTileLayerConfig(mapStyle);

    // Add tiles with configured style
    L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
    }).addTo(this.stageSelectorMap);

    // Add markers
    const validMarkers = navConfig.markers.filter(m => {
      const pos = m.geoPosition || this.experienceConfig?.stages.find(s => s.id === m.stageId)?.location;
      return pos && (pos.lat !== 0 || pos.lng !== 0);
    });

    const bounds: [number, number][] = [];

    validMarkers.forEach(marker => {
      const pos = marker.geoPosition || this.experienceConfig?.stages.find(s => s.id === marker.stageId)?.location;
      if (!pos) return;

      const stage = this.experienceConfig?.stages.find(s => s.id === marker.stageId);
      const label = marker.label || stage?.name || marker.stageId;
      const isCurrent = this.experienceConfig?.stages[this.currentStageIndex]?.id === marker.stageId;

      const markerIcon = L.divIcon({
        className: `stage-selector-map-marker ${isCurrent ? 'current' : ''}`,
        html: `<div class="marker-dot"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });

      // Non-null assertion: stageSelectorMap is set at line 1678 before this forEach runs
      const leafletMarker = L.marker([pos.lat, pos.lng], { icon: markerIcon })
        .addTo(this.stageSelectorMap!)
        .bindTooltip(label, { permanent: false, direction: 'top' });

      leafletMarker.on('click', () => {
        const stageIndex = this.experienceConfig?.stages.findIndex(s => s.id === marker.stageId) ?? -1;
        if (stageIndex >= 0) {
          this.jumpToStage(stageIndex);
        }
      });

      bounds.push([pos.lat, pos.lng]);
    });

    // Fit map to markers
    if (bounds.length > 0 && this.stageSelectorMap) {
      if (bounds.length === 1) {
        this.stageSelectorMap.setView(bounds[0], 12);
      } else {
        this.stageSelectorMap.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
      }
    }
  }

  /**
   * Load Leaflet library
   */
  private async loadLeaflet(): Promise<typeof L | null> {
    if (window.L) return window.L;

    return new Promise((resolve) => {
      // Load CSS
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      // Load JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => resolve(window.L ?? null);
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    });
  }


  /**
   * Initialize floor plan inside stage selector
   */
  private initializeStageSelectorFloorplan(): void {
    if (!this.stageSelectorFloorplanContainer || !this.experienceConfig?.navigation) return;

    const navConfig = this.experienceConfig.navigation;
    if (navConfig.type !== 'floorplan') {
      this.renderEmptyState(this.stageSelectorFloorplanContainer, 'floorplan');
      return;
    }

    const floorPlanUrl = navConfig.floorPlan?.imageUrl || navConfig.floorPlans?.[0]?.imageUrl;
    if (!floorPlanUrl) {
      this.renderEmptyState(this.stageSelectorFloorplanContainer, 'floorplan');
      return;
    }

    // Render floor plan with markers (using HTML-positioned pins like editor)
    this.stageSelectorFloorplanContainer.innerHTML = `
      <div class="floor-plan-image-wrapper">
        <img src="${floorPlanUrl}" alt="Floor Plan" class="floor-plan-image" />
      </div>
    `;

    // Add markers as individual positioned elements (not SVG overlay)
    const imageWrapper = this.stageSelectorFloorplanContainer.querySelector('.floor-plan-image-wrapper');
    if (!imageWrapper) return;

    navConfig.markers.forEach(marker => {
      if (!marker.floorPlanPosition) return;

      const stage = this.experienceConfig?.stages.find(s => s.id === marker.stageId);
      const label = marker.label || stage?.name || marker.stageId;
      const isCurrent = this.experienceConfig?.stages[this.currentStageIndex]?.id === marker.stageId;
      const pinColor = isCurrent ? '#FF3B30' : '#34C759'; // Red for current, green for others

      // Create teardrop pin SVG (same as editor)
      const pinSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      pinSvg.setAttribute('class', `floor-plan-pin ${isCurrent ? 'current' : ''}`);
      pinSvg.setAttribute('viewBox', '0 0 24 32');
      pinSvg.style.cssText = `
        position: absolute;
        left: ${marker.floorPlanPosition.x * 100}%;
        top: ${marker.floorPlanPosition.y * 100}%;
        width: ${isCurrent ? '28px' : '22px'};
        height: ${isCurrent ? '36px' : '28px'};
        transform: translate(-50%, -100%);
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
        cursor: pointer;
        transition: transform 0.15s ease, filter 0.15s ease;
        pointer-events: auto;
      `;

      // Teardrop path (same as editor)
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('fill', pinColor);
      path.setAttribute('d', 'M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12zm0 16c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z');
      pinSvg.appendChild(path);

      // White center dot
      const centerDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      centerDot.setAttribute('fill', 'white');
      centerDot.setAttribute('cx', '12');
      centerDot.setAttribute('cy', '12');
      centerDot.setAttribute('r', isCurrent ? '3' : '2');
      pinSvg.appendChild(centerDot);

      // Hover effect
      pinSvg.addEventListener('mouseenter', () => {
        pinSvg.style.transform = 'translate(-50%, -100%) scale(1.15)';
        pinSvg.style.filter = 'drop-shadow(0 3px 6px rgba(0,0,0,0.6))';
      });
      pinSvg.addEventListener('mouseleave', () => {
        pinSvg.style.transform = 'translate(-50%, -100%)';
        pinSvg.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))';
      });

      // Click handler
      pinSvg.addEventListener('click', () => {
        const stageIndex = this.experienceConfig?.stages.findIndex(s => s.id === marker.stageId) ?? -1;
        if (stageIndex >= 0) {
          this.jumpToStage(stageIndex);
        }
      });

      imageWrapper.appendChild(pinSvg);

      // Add label below pin
      const labelEl = document.createElement('span');
      labelEl.className = `floor-plan-pin-label ${isCurrent ? 'current' : ''}`;
      labelEl.textContent = label;
      labelEl.style.cssText = `
        position: absolute;
        left: ${marker.floorPlanPosition.x * 100}%;
        top: ${marker.floorPlanPosition.y * 100}%;
        transform: translate(-50%, 4px);
        font-size: 11px;
        font-weight: ${isCurrent ? '600' : '500'};
        color: ${isCurrent ? '#FF3B30' : 'rgba(255,255,255,0.85)'};
        text-shadow: 0 1px 3px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.5);
        white-space: nowrap;
        pointer-events: none;
      `;
      imageWrapper.appendChild(labelEl);
    });
  }

  /**
   * Render empty state for unavailable navigation type
   */
  private renderEmptyState(container: HTMLElement, type: 'map' | 'floorplan', error?: string): void {
    const icon = type === 'map'
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>';

    let message = type === 'map'
      ? 'No map configured for this experience'
      : 'No floor plan configured for this experience';

    // Use custom error message if provided
    if (error) {
      message = error;
    }

    container.innerHTML = `
      <div class="stage-selector-empty-state">
        ${icon}
        <p>${message}</p>
      </div>
    `;
  }

  /**
   * Jump directly to a specific stage
   */
  private async jumpToStage(index: number): Promise<void> {
    if (!this.experienceConfig || index < 0 || index >= this.experienceConfig.stages.length) {
      return;
    }

    // Don't reload the current stage
    if (index === this.currentStageIndex) {
      logger.debug('Already on this stage, ignoring');
      return;
    }

    // Prevent spamming during active transition
    if (this.isLoading) {
      logger.debug('Already transitioning, ignoring...');
      return;
    }

    this.isLoading = true; // Lock navigation during transition

    const previousIndex = this.currentStageIndex;
    logger.info(`Jumping to stage ${index}${this.disableTransitions ? ' (instant)' : ''}`);

    // Close stage selector immediately
    this.closeStageSelector();

    // Update UI immediately to show destination stage info
    this.currentStageIndex = index;
    const targetStage = this.experienceConfig.stages[index];
    this.updateSceneNameLabel(targetStage.name || targetStage.id);
    this.updateStageCounter();

    // Update navigation overlay
    this.navigationManager?.updateCurrentStageByIndex(index);

    // Check if source stage has a floor arrow pointing to destination
    const floorArrowHotspot = this.findFloorArrowToStage(previousIndex, targetStage.id);

    // If transitions are disabled OR there's a floor arrow, load stage directly
    if (this.disableTransitions || floorArrowHotspot) {
      await this.loadStageDirectly(previousIndex, 'jump', floorArrowHotspot, false);
      return;
    }

    // Start portal expansion transition from center
    const portalPreviewManager = this.sceneManager.getPortalPreviewManager();
    await portalPreviewManager.startCenterTransition(targetStage, async () => {
      // This runs AFTER the portal has fully expanded
      logger.debug('Portal expansion complete, transferring texture to main dome');

      // Transfer texture from portal preview (auto-finds last center transition)
      const transferredTexture = portalPreviewManager.transferTexture();
      logger.debug('Transferred texture:', transferredTexture !== null);

      // Track stage navigation
      this.analyticsService?.trackStageNavigation(
        previousIndex,
        index,
        'jump',
        this.experienceConfig!.stages.length
      );

      // Load new stage FIRST (swaps main dome texture)
      await this.loadCurrentStage(transferredTexture);

      // Dispose only the center transition preview AFTER main dome has new texture
      portalPreviewManager.disposeCenterTransition();

      this.isLoading = false; // Unlock navigation after complete transition
    });
  }


  /**
   * Update progress bar based on video playback
   */
  private updateProgressBar(): void {
    const domeComponent = this.sceneManager.getDomeComponent();
    const video = domeComponent.getVideoElement();

    if (video && video.duration > 0) {
      const progress = video.currentTime / video.duration;
      if (this.progressBarFill && this.progressBarContainer) {
        this.progressBarFill.style.width = `${progress * 100}%`;
        this.progressBarContainer.classList.add('visible');
      }
    } else {
      // Hide progress bar if not a video
      if (this.progressBarContainer) {
        this.progressBarContainer.classList.remove('visible');
      }
    }
  }

  /**
   * Start updating progress bar
   */
  private startProgressBarUpdates(): void {
    this.stopProgressBarUpdates();

    this.progressUpdateInterval = window.setInterval(() => {
      this.updateProgressBar();
    }, 100); // Update every 100ms
  }

  /**
   * Stop updating progress bar
   */
  private stopProgressBarUpdates(): void {
    if (this.progressUpdateInterval !== null) {
      clearInterval(this.progressUpdateInterval);
      this.progressUpdateInterval = null;
    }
  }

  /**
   * Handle click on progress bar to seek video
   */
  private handleProgressBarClick(e: MouseEvent): void {
    if (!this.progressBarContainer) return;

    const domeComponent = this.sceneManager.getDomeComponent();
    const video = domeComponent.getVideoElement();

    if (!video || !video.duration) {
      logger.debug('Cannot seek - no video or no duration');
      return;
    }

    // Calculate click position relative to progress bar
    const rect = this.progressBarContainer.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));

    // Seek to position
    const newTime = percentage * video.duration;
    video.currentTime = newTime;

    // Update progress bar immediately for responsive feel
    if (this.progressBarFill) {
      this.progressBarFill.style.width = `${percentage * 100}%`;
    }

    logger.debug(`Seeked to ${newTime.toFixed(1)}s (${(percentage * 100).toFixed(0)}%)`);
  }


  /**
   * Hide loading indicator (both desktop and VR)
   */
  private hideLoading(): void {
    // Hide 2D loading indicator
    this.loadingUI.hide();
    this.isLoading = false;
    
    // Hide VR loading indicator
    this.hideVRLoading();
  }

  /**
   * Show error message with retry button
   */
  private showErrorMessage(message: string, retryCallback: () => void): void {
    // Create error overlay if it doesn't exist
    let errorOverlay = document.getElementById('error-overlay');
    if (!errorOverlay) {
      errorOverlay = document.createElement('div');
      errorOverlay.id = 'error-overlay';
      errorOverlay.className = 'fullscreen-modal show';
      errorOverlay.style.zIndex = '10000';
      document.body.appendChild(errorOverlay);
    }

    // Set error message
    errorOverlay.innerHTML = `
      <div class="ios-card" style="background: rgba(255, 255, 255, 0.95); max-width: 400px; text-align: center;">
        <h2 style="margin: 0 0 12px 0; font-size: 24px; color: var(--red-primary);">Error Loading Stage</h2>
        <p style="margin: 0 0 24px 0; font-size: 15px; color: var(--ios-gray); line-height: 1.5;">${message}</p>
        <div style="display: flex; gap: 12px; justify-content: center;">
          <button id="retry-button" class="ios-button ios-button-primary">Retry</button>
          <button id="dismiss-button" class="ios-button ios-button-secondary">Dismiss</button>
        </div>
      </div>
    `;

    // Show overlay
    errorOverlay.style.display = 'flex';

    // Add retry button listener
    const retryButton = document.getElementById('retry-button');
    if (retryButton) {
      retryButton.addEventListener('click', () => {
        errorOverlay!.style.display = 'none';
        retryCallback();
      }, { once: true });
    }

    // Add dismiss button listener
    const dismissButton = document.getElementById('dismiss-button');
    if (dismissButton) {
      dismissButton.addEventListener('click', () => {
        errorOverlay!.style.display = 'none';
      }, { once: true });
    }
  }

  /**
   * Enable or disable navigation controls visually
   */
  private setNavigationEnabled(enabled: boolean): void {
    // Toggle disabled class on buttons
    if (this.prevButton) {
      this.prevButton.classList.toggle('disabled', !enabled);
    }
    if (this.nextButton) {
      this.nextButton.classList.toggle('disabled', !enabled);
    }

    // Toggle loading effect on nav controls container
    if (this.navControls) {
      this.navControls.classList.toggle('loading', !enabled);

      // Add/remove SVG loader
      if (!enabled) {
        this.addSVGLoader();
      } else {
        this.removeSVGLoader();
      }
    }

    // Also disable stage list items if selector is open
    if (this.stageList) {
      const items = this.stageList.querySelectorAll('.stage-item');
      items.forEach(item => {
        item.classList.toggle('disabled', !enabled);
        // Ensure pointer events are handled by CSS, but explicit check helps too
        (item as HTMLElement).style.pointerEvents = enabled ? '' : 'none';
      });
    }

    // Disable VR menu items if needed
    if (this.vrStageList) {
      this.vrStageList.children.forEach(control => {
        control.isEnabled = enabled;
        // Visual feedback for VR items (dim them)
        if (!enabled) {
          control.alpha = 0.5;
        } else {
          control.alpha = 1.0;
        }
      });
    }
  }

  /**
   * Navigate to next stage
   */
  private async nextStage(): Promise<void> {
    if (!this.experienceConfig || this.experienceConfig.stages.length === 0) {
      logger.warn('No stages configured');
      return;
    }

    // Prevent spamming during active transition
    if (this.isLoading) {
      logger.debug('Already transitioning, ignoring...');
      return;
    }

    this.isLoading = true; // Lock navigation during transition
    this.setNavigationEnabled(false); // Visually disable

    const previousIndex = this.currentStageIndex;
    const nextIndex = (this.currentStageIndex + 1) % this.experienceConfig.stages.length;
    logger.debug(`Stage ${previousIndex} -> ${nextIndex}${this.disableTransitions ? ' (instant)' : ''}`);

    // Update UI immediately to show destination stage info
    this.currentStageIndex = nextIndex;
    const targetStage = this.experienceConfig.stages[nextIndex];
    this.updateSceneNameLabel(targetStage.name || targetStage.id);
    this.updateStageCounter();

    // Update navigation overlay
    this.navigationManager?.updateCurrentStageByIndex(nextIndex);

    // Check if source stage has a floor arrow pointing to destination
    const floorArrowHotspot = this.findFloorArrowToStage(previousIndex, targetStage.id);

    // If transitions are disabled OR there's a floor arrow, load stage directly
    if (this.disableTransitions || floorArrowHotspot) {
      await this.loadStageDirectly(previousIndex, 'next', floorArrowHotspot, true);
      return;
    }

    // Start portal expansion transition
    const portalPreviewManager = this.sceneManager.getPortalPreviewManager();

    // If in free-look mode, expand from gaze portal position
    if (this.isFreeLookPortalMode) {
      // Convert gaze portal to expanding transition
      await portalPreviewManager.expandGazePortalToTransition(targetStage, async () => {
        // This runs AFTER the portal has fully expanded
        logger.debug('Gaze portal expansion complete');

        // Transfer texture from gaze portal
        const transferredTexture = portalPreviewManager.transferTexture('_gaze_portal');
        logger.debug('Transferred texture:', transferredTexture !== null);

        // Track stage navigation
        this.analyticsService?.trackStageNavigation(
          previousIndex,
          this.currentStageIndex,
          'next',
          this.experienceConfig!.stages.length
        );

        // Load new stage
        await this.loadCurrentStage(transferredTexture);

        // Clean up old gaze portal and create new one for next stage (stay in gaze mode)
        portalPreviewManager.hideGazePortal();

        const newNextIndex = (this.currentStageIndex + 1) % this.experienceConfig!.stages.length;
        const newNextStage = this.experienceConfig!.stages[newNextIndex];
        await portalPreviewManager.showGazePortal(newNextStage);

        this.isLoading = false;
        this.setNavigationEnabled(true);
      });
    } else {
      // Normal center transition
      await portalPreviewManager.startCenterTransition(targetStage, async () => {
        // This runs AFTER the portal has fully expanded
        logger.debug('Portal expansion complete, transferring texture to main dome');

        // Transfer texture from portal preview (auto-finds last center transition)
        const transferredTexture = portalPreviewManager.transferTexture();
        logger.debug('Transferred texture:', transferredTexture !== null);

        // Track stage navigation
        this.analyticsService?.trackStageNavigation(
          previousIndex,
          this.currentStageIndex,
          'next',
          this.experienceConfig!.stages.length
        );

        // Load new stage FIRST (swaps main dome texture)
        await this.loadCurrentStage(transferredTexture);

        // Dispose only the center transition preview AFTER main dome has new texture
        portalPreviewManager.disposeCenterTransition();

        this.isLoading = false; // Unlock navigation after complete transition
        this.setNavigationEnabled(true); // Re-enable
      });
    }
  }

  /**
   * Setup keyboard shortcuts for navigation and controls
   */
  private setupKeyboardShortcuts(): void {
    // Store reference for cleanup
    this.keyboardHandler = (e: KeyboardEvent) => {
      // Don't handle shortcuts when typing in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      switch (e.key) {
        case ' ': // Space bar - toggle video play/pause
          e.preventDefault();
          this.toggleVideoPlayback();
          break;
        case 'm':
        case 'M': // Mute/unmute audio
          this.toggleAudioMute();
          break;
        case 'Escape':
          // Close any open modals/selectors
          this.closeStageSelector();
          break;
      }
    };

    document.addEventListener('keydown', this.keyboardHandler);
  }

  // Store keyboard handler reference for cleanup
  private keyboardHandler: ((e: KeyboardEvent) => void) | null = null;

  /**
   * Navigate to previous stage
   */
  private async previousStage(): Promise<void> {
    if (!this.experienceConfig || this.experienceConfig.stages.length === 0) {
      logger.warn('No stages configured');
      return;
    }

    // Prevent spamming during active transition
    if (this.isLoading) {
      logger.debug('Already transitioning, ignoring...');
      return;
    }

    this.isLoading = true; // Lock navigation during transition
    this.setNavigationEnabled(false); // Visually disable

    const previousIndex = this.currentStageIndex;
    const prevIndex = (this.currentStageIndex - 1 + this.experienceConfig.stages.length) % this.experienceConfig.stages.length;
    logger.debug(`Stage ${previousIndex} -> ${prevIndex}${this.disableTransitions ? ' (instant)' : ''}`);

    // Update UI immediately to show destination stage info
    this.currentStageIndex = prevIndex;
    const targetStage = this.experienceConfig.stages[prevIndex];
    this.updateSceneNameLabel(targetStage.name || targetStage.id);
    this.updateStageCounter();

    // Update navigation overlay
    this.navigationManager?.updateCurrentStageByIndex(prevIndex);

    // Check if source stage has a floor arrow pointing to destination
    const floorArrowHotspot = this.findFloorArrowToStage(previousIndex, targetStage.id);

    // If transitions are disabled OR there's a floor arrow, load stage directly
    if (this.disableTransitions || floorArrowHotspot) {
      await this.loadStageDirectly(previousIndex, 'previous', floorArrowHotspot, true);
      return;
    }

    // Start portal expansion transition
    const portalPreviewManager = this.sceneManager.getPortalPreviewManager();

    // If in free-look mode, expand from gaze portal position
    if (this.isFreeLookPortalMode) {
      // Calculate what the new "next" stage will be after navigating
      const newPrevIndex = (this.currentStageIndex - 1 + this.experienceConfig.stages.length) % this.experienceConfig.stages.length;
      const newPrevStage = this.experienceConfig.stages[newPrevIndex];

      // DON'T recreate - just expand the existing gaze portal
      // Convert gaze portal to expanding transition
      await portalPreviewManager.expandGazePortalToTransition(targetStage, async () => {
        // This runs AFTER the portal has fully expanded
        logger.debug('Gaze portal expansion complete');

        // Transfer texture from gaze portal
        const transferredTexture = portalPreviewManager.transferTexture('_gaze_portal');
        logger.debug('Transferred texture:', transferredTexture !== null);

        // Track stage navigation
        this.analyticsService?.trackStageNavigation(
          previousIndex,
          this.currentStageIndex,
          'previous',
          this.experienceConfig!.stages.length
        );

        // Load new stage
        await this.loadCurrentStage(transferredTexture);

        // Clean up old gaze portal and create new one for previous stage (stay in gaze mode)
        portalPreviewManager.hideGazePortal();

        await portalPreviewManager.showGazePortal(newPrevStage);

        this.isLoading = false;
        this.setNavigationEnabled(true);
      });
    } else {
      // Normal center transition
      await portalPreviewManager.startCenterTransition(targetStage, async () => {
        // This runs AFTER the portal has fully expanded
        logger.debug('Portal expansion complete, transferring texture to main dome');

        // Transfer texture from portal preview (auto-finds last center transition)
        const transferredTexture = portalPreviewManager.transferTexture();
        logger.debug('Transferred texture:', transferredTexture !== null);

        // Track stage navigation
        this.analyticsService?.trackStageNavigation(
          previousIndex,
          this.currentStageIndex,
          'previous',
          this.experienceConfig!.stages.length
        );

        // Load new stage FIRST (swaps main dome texture)
        await this.loadCurrentStage(transferredTexture);

        // Dispose only the center transition preview AFTER main dome has new texture
        portalPreviewManager.disposeCenterTransition();

        this.isLoading = false; // Unlock navigation after complete transition
        this.setNavigationEnabled(true); // Re-enable
      });
    }
  }

  /**
   * Load the current stage index
   * @param transferredTexture Optional texture to transfer from portal preview
   * @param progressOffset Base progress percentage (0-100)
   * @param progressScale Scale factor for progress (0-1)
   */
  async loadCurrentStage(
    transferredTexture?: { texture: Texture | VideoTexture, type: 'image' | 'video', rotation?: number } | null,
    progressOffset: number = 0,
    progressScale: number = 1
  ): Promise<void> {
    if (!this.experienceConfig) return;



    const stage = this.experienceConfig.stages[this.currentStageIndex];
    if (!stage) return;

    logger.info(`Loading stage ${this.currentStageIndex + 1}/${this.experienceConfig.stages.length}:`, stage.id);

    // Track stage view started
    this.analyticsService?.trackStageViewStarted(
      this.currentStageIndex,
      stage.name,
      stage.skybox?.type === 'video',
      !!stage.models && stage.models.length > 0,
      !!stage.planes && stage.planes.length > 0,
      !!stage.audioUrl
    );

    this.isLoading = true;
    this.updateButtonVisibility();
    
    // Update UI
    this.updateSceneNameLabel(stage.name || stage.id);
    this.updateStageCounter();

    // Show loading screen (debounced)
    // Only show loading screen on the very first load
    // STRICT CHECK: Do not even schedule the timeout if initial load is complete
    let loadingDebounce: NodeJS.Timeout | null = null;
    
    if (!this.hasInitialLoadCompleted) {
      loadingDebounce = setTimeout(() => {
        if (this.isLoading && !this.hasInitialLoadCompleted) {
          if (this.isInVR()) {
            this.showVRLoading();
          } else {
            this.loadingUI.show();
          }
        }
      }, 200);
    } else {
      logger.debug('Skipping loading screen (initial load completed)');
    }

    try {
      await this.sceneManager.loadStage(
        stage, 
        transferredTexture || undefined,
        (percent, status) => {
          // Scale progress
          const scaledPercent = progressOffset + (percent * progressScale);
          
          if (this.isInVR()) {
            if (this.vrLoadingText) {
              this.vrLoadingText.text = status;
            }
          } else {
            this.loadingUI.updateProgress(scaledPercent, status);
          }
        },
        this.currentStageIndex,
        this.experienceConfig.stages.length
      );

      if (loadingDebounce) {
        clearTimeout(loadingDebounce);
      }

      // Orient camera to initialCameraTarget ONLY for the first stage on initial load
      // Portal navigation handles camera orientation separately via initialViewDirection
      if (this.currentStageIndex === 0 && !this.hasInitialLoadCompleted && stage.initialCameraTarget) {
        this.orientCameraToTarget(stage.initialCameraTarget);
      }

      // Update scene name label
      this.updateSceneNameLabel(stage.name || stage.id);

      // Update stage counter
      this.updateStageCounter();

      // Update play/pause button visibility
      this.updatePlayPauseButtonVisibility();

      // Update audio controls visibility
      this.updateAudioControlsVisibility();

      // Start progress bar if it's a video, hide if not
      if (stage.skybox && stage.skybox.type === 'video') {
        this.startProgressBarUpdates();

        // Handle video audio based on user interaction state
        const videoElement = this.sceneManager.getDomeComponent().getVideoElement();
        if (videoElement) {
          if (this.hasUserInteractedForAudio) {
            // User has already interacted - apply volume setting normally
            const volume = this.audioVolumeSlider
              ? parseInt(this.audioVolumeSlider.value) / 100
              : 1.0;
            videoElement.muted = volume === 0;
            videoElement.volume = volume;
            logger.debug(`Applied volume ${volume} to video skybox (muted: ${volume === 0})`);
          } else {
            // No user interaction yet - keep video muted
            // This is required by browser autoplay policy
            videoElement.muted = true;
            logger.debug('Video kept muted - waiting for user interaction');

            // Only show tap-to-hear overlay on FIRST stage load (not subsequent navigations)
            // This prevents the overlay from appearing when navigating to video stages
            if (!this.hasInitialLoadCompleted) {
              // Show tap-to-hear overlay after a short delay to let video load
              setTimeout(() => {
                // Only show if this is still a video stage and no interaction yet
                const currentStage = this.experienceConfig?.stages[this.currentStageIndex];
                if (currentStage?.skybox?.type === 'video' && !this.hasUserInteractedForAudio) {
                  this.showTapToHearAudioOverlay();
                }
              }, 500);
            }
          }
        }

        // Always hide overlay if user has interacted
        if (this.hasUserInteractedForAudio) {
          this.hideTapToHearAudioOverlay();
        }
      } else {
        // Not a video stage
        this.stopProgressBarUpdates();

        // Check if this is first stage with any autoplay audio:
        // 1. Scene background audio (stage.audioUrl)
        // 2. Audio hotspots with audioAutoplay enabled
        // 3. Global tour audio
        const hasAutoplayAudioHotspot = stage.hotspots?.some(
          (h: HotspotConfig) => (h.type === 'audio' || h.type === 'both') && h.audioUrl && h.audioAutoplay
        );
        const hasGlobalAudio = this.experienceConfig?.globalAudio?.url;
        const hasAutoplayAudio = stage.audioUrl || hasAutoplayAudioHotspot || hasGlobalAudio;

        if (!this.hasInitialLoadCompleted && !this.hasUserInteractedForAudio && hasAutoplayAudio) {
          logger.debug('First stage with autoplay audio - showing tap-to-hear overlay');
          setTimeout(() => {
            if (!this.hasUserInteractedForAudio && !this.hasInitialLoadCompleted) {
              this.showTapToHearAudioOverlay();
            }
          }, 500);
        } else {
          // Hide overlay if it was showing
          this.hideTapToHearAudioOverlay();
        }
        // Hide and reset progress bar for non-video stages
        if (this.progressBarContainer) {
          this.progressBarContainer.classList.remove('visible');
        }
        if (this.progressBarFill) {
          this.progressBarFill.style.width = '0%';
        }
      }

      // Hide loading indicator
      if (this.isInVR()) {
        this.hideVRLoading();
      } else {
        this.loadingUI.hide();
      }

      // Preload next stage in background
      this.preloadNextStage();

    } catch (error) {
      logger.error('Failed to load stage:', error);
      // Don't hide loading here, let setError handle it (or keep it shown with error)
      this.stopProgressBarUpdates();

      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      if (this.isInVR()) {
        // Fallback for VR error (simple text for now)
        if (this.vrLoadingText) {
          this.vrLoadingText.text = `Error: ${errorMessage}`;
          this.vrLoadingText.color = 'red';
        }
      } else {
        // Use LoadingUI error display
        this.loadingUI.setError(errorMessage);
        
        this.hideLoading(); 
        this.showErrorMessage(`Failed to load stage: ${errorMessage}`, () => {
          // Retry callback
          logger.info('User requested retry for stage load');
          this.loadCurrentStage();
        });
      }
    } finally {
      // Always unlock navigation, even if error occurred
      this.isLoading = false;

      // Ensure we mark initial load as completed regardless of success/failure
      // This prevents the loading screen from reappearing on navigation
      if (!this.hasInitialLoadCompleted) {
        logger.info('Initial load phase completed');
        this.hasInitialLoadCompleted = true;

        // Start the drag-to-look helper timer after initial load
        this.startDragToLookTimer();
      }
    }
  }

  /**
   * Update the scene name label
   */
  private updateSceneNameLabel(name: string): void {
    if (!this.sceneNameLabel) return;
    this.sceneNameLabel.textContent = name;

    // Also update hand tracking nav label if visible
    if (this.handNavVisible) {
      this.updateHandNavLabel();
    }
  }

  /**
   * Update the stage counter display
   */
  private updateStageCounter(): void {
    if (!this.stageCounterInline || !this.experienceConfig) return;

    const currentNum = this.currentStageIndex + 1;
    const totalNum = this.experienceConfig.stages.length;
    this.stageCounterInline.textContent = `${currentNum} / ${totalNum}`;
  }

  /**
   * Preload the next stage in background
   */
  private preloadNextStage(): void {
    if (!this.experienceConfig) return;

    const nextIndex = (this.currentStageIndex + 1) % this.experienceConfig.stages.length;

    // Don't preload if already preloaded
    if (this.preloadedStages.get(nextIndex)) {
      return;
    }

    const nextStage = this.experienceConfig.stages[nextIndex];

    logger.debug(`Preloading next stage: ${nextStage.id}`);

    // Preload skybox if exists
    if (nextStage.skybox) {
      if (nextStage.skybox.type === 'image' && nextStage.skybox.url) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          logger.debug(`Preloaded image: ${nextStage.id}`);
          this.preloadedStages.set(nextIndex, true);
        };
        img.src = nextStage.skybox.url;
      } else if (nextStage.skybox.type === 'video' && nextStage.skybox.url) {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.preload = 'auto';
        video.src = nextStage.skybox.url;
        video.load();
        logger.debug(`Preloading video: ${nextStage.id}`);
        this.preloadedStages.set(nextIndex, true);
      } else if (nextStage.skybox.type === 'color') {
        // Color skybox doesn't need preloading
        logger.debug(`Color skybox, no preload needed: ${nextStage.id}`);
        this.preloadedStages.set(nextIndex, true);
      }
    }

    // Preload audio if exists
    if (nextStage.audioUrl) {
      const audio = new Audio();
      audio.crossOrigin = 'anonymous';
      audio.preload = 'auto';
      audio.src = nextStage.audioUrl;
      audio.load();
      logger.debug(`Preloading audio: ${nextStage.audioUrl}`);
    }
  }

  /**
   * Update button visibility based on stage count
   */
  private updateButtonVisibility(): void {
    if (!this.experienceConfig) return;

    const hasMultipleStages = this.experienceConfig.stages.length > 1;
    const hasStages = this.experienceConfig.stages.length > 0;

    // Always show nav controls container if there are stages (to show title)
    if (this.navControls) {
      if (hasStages) {
        this.navControls.classList.remove('hidden');
      } else {
        this.navControls.classList.add('hidden');
      }
    }

    // Show/hide navigation arrows based on multiple stages
    if (this.prevButton) {
      if (hasMultipleStages) {
        this.prevButton.classList.remove('hidden');
      } else {
        this.prevButton.classList.add('hidden');
      }
    }

    if (this.nextButton) {
      if (hasMultipleStages) {
        this.nextButton.classList.remove('hidden');
      } else {
        this.nextButton.classList.add('hidden');
      }
    }

    // Show/hide inline stage counter
    if (this.stageCounterInline) {
      if (hasMultipleStages) {
        this.stageCounterInline.classList.remove('hidden');
      } else {
        this.stageCounterInline.classList.add('hidden');
      }
    }

    // Show control buttons container (VR controls toggle + fullscreen)
    if (this.controlButtonsContainer) {
      this.controlButtonsContainer.classList.remove('hidden');
    }
  }

  /**
   * Setup VR controller button mapping - A/B buttons + triggers
   */
  setupControllerMapping(xrHelper: XRHelper): void {
    const xr = xrHelper.getXRExperience();
    if (!xr) return;

    // Debounce map to prevent rapid-fire triggering
    const triggerDebounce = new Map<string, boolean>();

    // Listen for controller input
    xr.input.onControllerAddedObservable.add((controller: WebXRInputSource) => {
      logger.debug('VR Controller connected:', controller.uniqueId);
      const handedness = controller.inputSource.handedness; // 'left' or 'right'
      logger.debug(`Controller handedness: ${handedness}`);

      // Detect hand tracking input sources (have inputSource.hand, no motion controller)
      if (controller.inputSource.hand) {
        logger.info(`Hand tracking detected: ${handedness} hand`);
        this.activeHandInputSources.add(controller.uniqueId);
        this.handTrackingActive = true;
        return; // Hand tracking sources don't have motion controllers
      }

      // Physical controller connected - hide hand nav if it was showing
      if (this.handTrackingActive) {
        this.activeHandInputSources.clear();
        this.handTrackingActive = false;
        this.setHandTrackingNavVisible(false);
      }

      // Map controller buttons
      controller.onMotionControllerInitObservable.add((motionController: WebXRAbstractMotionController) => {
        logger.debug(`Motion controller initialized - ${handedness} hand`);

        // RIGHT CONTROLLER - A and B buttons for navigation
        if (handedness === 'right') {
          // A button - Select hovered menu item or Previous stage (flipped)
          const aButton = motionController.getComponent('a-button');
          if (aButton) {
            aButton.onButtonStateChangedObservable.add((component: WebXRControllerComponent) => {
              if (component.pressed) {
                if (this.isStageSelectorOpen) {
                  logger.debug('A button pressed - Select hovered menu item');
                  this.selectHoveredMenuItem();
                } else {
                  logger.debug('A button pressed - Previous stage');
                  this.previousStage();
                }
              }
            });
          }

          // B button - Close menu or Next stage (flipped)
          const bButton = motionController.getComponent('b-button');
          if (bButton) {
            bButton.onButtonStateChangedObservable.add((component: WebXRControllerComponent) => {
              if (component.pressed) {
                if (this.isStageSelectorOpen) {
                  logger.debug('B button pressed - Close menu');
                  this.closeVRStageSelector();
                } else {
                  logger.debug('B button pressed - Next stage');
                  this.nextStage();
                }
              }
            });
          }
        }

        // LEFT CONTROLLER - X for menu, Y for play/pause
        if (handedness === 'left') {
          logger.debug('Looking for X and Y buttons...');

          // List all available components for debugging
          const components = motionController.components;
          logger.debug('Available components:', Object.keys(components));

          // X button - Toggle stage selector
          const xButton = motionController.getComponent('x-button');
          logger.debug('X button found:', !!xButton);
          if (xButton) {
            xButton.onButtonStateChangedObservable.add((component: WebXRControllerComponent) => {
              logger.debug('X button state changed - pressed:', component.pressed);
              if (component.pressed) {
                logger.debug('X button pressed - Toggle stage selector');
                this.toggleStageSelector();
              }
            });
          }

          // Y button - Play/Pause video
          const yButton = motionController.getComponent('y-button');
          logger.debug('Y button found:', !!yButton);
          if (yButton) {
            yButton.onButtonStateChangedObservable.add((component: WebXRControllerComponent) => {
              logger.debug('Y button state changed - pressed:', component.pressed);
              if (component.pressed) {
                logger.debug('Y button pressed - Toggle play/pause');
                this.toggleVideoPlayback();
              }
            });
          }

          // LEFT GRIP - Toggle free-look portal mode
          const leftGrip = motionController.getComponent('xr-standard-squeeze');
          if (leftGrip) {
            leftGrip.onButtonStateChangedObservable.add((component: WebXRControllerComponent) => {
              if (component.pressed) {
                logger.debug('Left grip pressed - Toggle free-look portal mode');
                this.toggleFreeLookPortalMode();
              }
            });
          }
        }

        // RIGHT GRIP - Toggle free-look portal mode (same as left for convenience)
        if (handedness === 'right') {
          const rightGrip = motionController.getComponent('xr-standard-squeeze');
          if (rightGrip) {
            rightGrip.onButtonStateChangedObservable.add((component: WebXRControllerComponent) => {
              if (component.pressed) {
                logger.debug('Right grip pressed - Toggle free-look portal mode');
                this.toggleFreeLookPortalMode();
              }
            });
          }

          // RIGHT THUMBSTICK - Control gaze portal transition progress
          const rightThumbstick = motionController.getComponent('xr-standard-thumbstick');
          if (rightThumbstick) {
            rightThumbstick.onAxisValueChangedObservable.add((axes: IWebXRMotionControllerAxesValue) => {
              if (!this.isFreeLookPortalMode) return;

              const yAxis = axes.y; // -1 to 1 (up = positive, down = negative)

              // Only react if moved significantly (deadzone)
              if (Math.abs(yAxis) > 0.3) {
                const portalPreviewManager = this.sceneManager.getPortalPreviewManager();
                const delta = yAxis * 0.005; // Smaller delta for smoother control

                portalPreviewManager.adjustGazePortalProgress(delta);
              }
            });
          }
        }

        // Trigger button with debouncing
        const trigger = motionController.getComponent('xr-standard-trigger');
        if (trigger) {
          const triggerKey = `${handedness}-trigger`;

          trigger.onButtonStateChangedObservable.add((component: WebXRControllerComponent) => {
            logger.debug(`Trigger state change - ${handedness} hand, pressed: ${component.pressed}`);

            if (component.pressed) {
              // Check debounce
              if (triggerDebounce.get(triggerKey)) {
                logger.debug(`${handedness} trigger debounced - ignoring`);
                return;
              }

              // Set debounce flag
              triggerDebounce.set(triggerKey, true);
              setTimeout(() => {
                triggerDebounce.set(triggerKey, false);
              }, 300); // 300ms debounce

              // Triggers only select hovered menu item when menu is open
              if (this.isStageSelectorOpen) {
                logger.debug(`${handedness} trigger pressed - Select hovered menu item`);
                this.selectHoveredMenuItem();
              }
              // Triggers do NOT cycle scenes when menu is closed
            }
          });
        } else {
          logger.warn(`No trigger found for ${handedness} controller`);
        }
      });
    });

    // Handle controller removal (hand tracking sources disappearing)
    xr.input.onControllerRemovedObservable.add((controller: WebXRInputSource) => {
      const removedId = controller.uniqueId;
      if (this.activeHandInputSources.has(removedId)) {
        this.activeHandInputSources.delete(removedId);
        logger.debug(`Hand tracking source removed: ${removedId}, remaining: ${this.activeHandInputSources.size}`);

        // Hide nav panel only if no hand tracking sources remain
        if (this.activeHandInputSources.size === 0) {
          this.handTrackingActive = false;
          this.setHandTrackingNavVisible(false);
        }
      }
    });
  }

  /**
   * Initialize and load first stage
   */
  async initialize(): Promise<void> {
    await this.loadConfig();
    this.updateButtonVisibility();

    if (this.experienceConfig && this.experienceConfig.stages.length > 0) {
      await this.loadCurrentStage();
    } else {
      logger.warn('No stages configured in config.json');
    }
  }

  /**
   * Toggle video playback (play/pause)
   */
  private async toggleVideoPlayback(): Promise<void> {
    const domeComponent = this.sceneManager.getDomeComponent();
    if (!domeComponent) return;

    const isPlaying = await domeComponent.isPlaying();

    // SVG icons for play/pause (white fill for visibility on dark backgrounds)
    const playSvg = `<svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>`;
    const pauseSvg = `<svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>`;

    if (isPlaying) {
      await domeComponent.pause();
      if (this.playPauseIcon) this.playPauseIcon.innerHTML = playSvg;
      if (this.playPauseBtn) this.playPauseBtn.classList.add('show-play-icon');
      logger.debug('Video paused');
    } else {
      await domeComponent.resume();
      if (this.playPauseIcon) this.playPauseIcon.innerHTML = pauseSvg;
      if (this.playPauseBtn) this.playPauseBtn.classList.remove('show-play-icon');
      logger.debug('Video resumed');
    }
  }

  /**
   * Update play/pause button visibility based on stage type
   */
  private updatePlayPauseButtonVisibility(): void {
    if (!this.playPauseBtn || !this.experienceConfig) return;

    const currentStage = this.experienceConfig.stages[this.currentStageIndex];
    const isVideoStage = currentStage?.skybox?.type === 'video';

    // SVG icons for play/pause (white fill for visibility on dark backgrounds)
    const playSvg = `<svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>`;
    const pauseSvg = `<svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>`;

    if (isVideoStage) {
      this.playPauseBtn.classList.remove('hidden');
      // Set initial state based on autoplay
      if (this.playPauseIcon) {
        const isPlaying = currentStage.skybox?.autoplay !== false;
        this.playPauseIcon.innerHTML = isPlaying ? pauseSvg : playSvg;

        if (isPlaying) {
          this.playPauseBtn.classList.remove('show-play-icon');
        } else {
          this.playPauseBtn.classList.add('show-play-icon');
        }
      }
    } else {
      this.playPauseBtn.classList.add('hidden');
    }
  }

  /**
   * Update audio controls visibility
   */
  private updateAudioControlsVisibility(): void {
    if (!this.experienceConfig) return;

    const currentStage = this.experienceConfig.stages[this.currentStageIndex];
    const hasStageAudio = !!currentStage?.audioUrl;
    const globalAudioConfig = this.experienceConfig.globalAudio;
    const hasGlobalAudio = !!globalAudioConfig?.url;

    if (hasStageAudio) {
      // Use audioTitle if available, otherwise extract filename from URL
      const displayTitle = currentStage.audioTitle ||
                         currentStage.audioUrl?.split('/').pop()?.split('?')[0] ||
                         'Background Audio';

      this.currentBackgroundAudio = displayTitle;

      // Update background audio title display
      if (this.backgroundAudioTitle) {
        this.backgroundAudioTitle.textContent = '♪ ' + displayTitle;
        this.backgroundAudioTitle.style.display = 'block';
      }

      logger.debug('Showing background audio: ' + displayTitle);
    } else if (hasGlobalAudio) {
      // Global tour audio is playing across all scenes
      const displayTitle = globalAudioConfig!.title ||
                         globalAudioConfig!.url?.split('/').pop()?.split('?')[0] ||
                         'Background Audio';

      this.currentBackgroundAudio = displayTitle;

      if (this.backgroundAudioTitle) {
        this.backgroundAudioTitle.textContent = '♪ ' + displayTitle;
        this.backgroundAudioTitle.style.display = 'block';
      }

      logger.debug('Showing global audio: ' + displayTitle);
    } else {
      this.currentBackgroundAudio = null;

      // Hide background audio title
      if (this.backgroundAudioTitle) {
        this.backgroundAudioTitle.style.display = 'none';
      }

      logger.debug('No background audio for this stage');
    }

    // Check for video with audio
    this.updateVideoAudioDisplay();

    // Update unified display
    this.updateAudioDisplay();
  }

  /**
   * Update video audio display
   * Checks if current video skybox has audio and shows the indicator
   */
  private updateVideoAudioDisplay(): void {
    if (!this.experienceConfig) return;

    const currentStage = this.experienceConfig.stages[this.currentStageIndex];
    const isVideoSkybox = currentStage?.skybox?.type === 'video';

    if (isVideoSkybox) {
      // Check if video has audio tracks
      const domeComponent = this.sceneManager.getDomeComponent();
      const videoElement = domeComponent.getVideoElement();

      if (videoElement) {
        // Check for audio tracks - mozHasAudio for Firefox, webkitAudioDecodedByteCount for Chrome
        // For most cases, we assume video has audio if it's a video type
        // The video.volume and video.muted properties work regardless
        const hasAudioTrack = this.checkVideoHasAudio(videoElement);

        if (hasAudioTrack) {
          this.currentVideoAudio = 'Video Audio';

          if (this.videoAudioTitle) {
            this.videoAudioTitle.textContent = '🎬 Video Audio';
            this.videoAudioTitle.style.display = 'block';
          }

          logger.debug('Video has audio - showing video audio controls');
          return;
        }
      }
    }

    // No video audio
    this.currentVideoAudio = null;
    if (this.videoAudioTitle) {
      this.videoAudioTitle.style.display = 'none';
    }
  }

  /**
   * Check if a video element has audio
   * Uses multiple detection methods for cross-browser compatibility
   */
  private checkVideoHasAudio(video: HTMLVideoElement): boolean {
    // Method 1: Check audioTracks API (standard but limited support)
    if (video.audioTracks && video.audioTracks.length > 0) {
      return true;
    }

    // Method 2: Firefox-specific
    if (video.mozHasAudio) {
      return true;
    }

    // Method 3: WebKit/Chrome - check if audio bytes are being decoded
    if (video.webkitAudioDecodedByteCount && video.webkitAudioDecodedByteCount > 0) {
      return true;
    }

    // Method 4: If video is not muted and has had time to load metadata,
    // assume it might have audio (conservative approach for user experience)
    // This ensures volume controls are shown for videos that likely have audio
    if (video.readyState >= 1) { // HAVE_METADATA or higher
      // Default to true for videos - user can mute if no audio
      return true;
    }

    return false;
  }

  /**
   * Setup tap-to-hear-audio hint overlay
   * Overlay is non-blocking (pointer-events: none), so we listen for scene interactions
   * to unmute audio and hide the hint
   */
  private setupTapToHearAudioOverlay(): void {
    // Listen for any user interaction on the canvas/document to unmute audio
    const handleInteraction = () => {
      if (this.hasUserInteractedForAudio) return;

      logger.info('User interaction detected - unmuting audio');
      this.hasUserInteractedForAudio = true;

      // Hide the overlay with animation
      this.hideTapToHearAudioOverlay();

      // Master multiplier from slider (default to 100% if slider not available)
      const master = this.audioVolumeSlider
        ? parseInt(this.audioVolumeSlider.value) / 100
        : 1.0;

      // Unmute video if this is a video stage
      const videoElement = this.sceneManager.getDomeComponent().getVideoElement();
      if (videoElement) {
        videoElement.muted = false;
        videoElement.volume = master;

        // Also ensure video is playing
        videoElement.play().then(() => {
          logger.info('Video unmuted and playing after user interaction');
        }).catch(err => {
          logger.warn('Video play failed:', err);
        });
      }

      // Also try to play background audio if present
      const bgAudio = this.sceneManager.getCurrentAudio();
      if (bgAudio) {
        try {
          const baselineVolume = this.experienceConfig?.stages[this.currentStageIndex]?.audioVolume ?? 1.0;
          bgAudio.setVolume(baselineVolume * master);
          this.sceneManager.playCurrentAudioOnce();
          logger.info('Background audio playing after user interaction');
        } catch (err) {
          logger.warn('Background audio play failed:', err);
        }
      }

      // Also unmute global audio if present — respect configured volume as ceiling
      const globalAudio = this.sceneManager.getGlobalAudio();
      if (globalAudio) {
        try {
          const baselineVolume = this.experienceConfig?.globalAudio?.volume ?? 1.0;
          globalAudio.setVolume(baselineVolume * master);
          this.sceneManager.playGlobalAudioOnce();
          logger.info('Global audio playing after user interaction');
        } catch (err) {
          logger.warn('Global audio play failed:', err);
        }
      }

      // Remove listeners after first interaction
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };

    // Listen for interactions on the document (overlay has pointer-events: none)
    document.addEventListener('click', handleInteraction);
    document.addEventListener('touchstart', handleInteraction, { passive: true });
    document.addEventListener('keydown', handleInteraction);
  }

  /**
   * Show tap-to-hear-audio hint
   */
  private showTapToHearAudioOverlay(): void {
    if (!this.tapToHearAudioOverlay || this.hasUserInteractedForAudio) return;

    logger.debug('Showing tap-to-hear-audio hint');
    this.tapToHearAudioOverlay.style.display = 'block';
    this.tapToHearAudioOverlay.classList.remove('hiding');
  }

  /**
   * Hide tap-to-hear-audio hint with animation
   */
  private hideTapToHearAudioOverlay(): void {
    if (!this.tapToHearAudioOverlay) return;

    this.tapToHearAudioOverlay.classList.add('hiding');

    // Remove after animation completes
    setTimeout(() => {
      if (this.tapToHearAudioOverlay) {
        this.tapToHearAudioOverlay.style.display = 'none';
        this.tapToHearAudioOverlay.classList.remove('hiding');
      }
    }, 300);
  }

  /**
   * Setup drag-to-look helper that monitors actual camera rotation changes
   */
  private setupDragToLookHelper(): void {
    // Store initial camera rotation when timer starts
    let initialAlpha: number | null = null;
    let initialBeta: number | null = null;

    // Check for camera rotation changes on each frame
    this.scene.onBeforeRenderObservable.add(() => {
      // Only check if we haven't detected rotation yet and timer is running
      if (this.hasUserDraggedToLook || !this.dragToLookTimeout) return;

      const camera = this.scene.activeCamera as ArcRotateCamera;
      if (!camera) return;

      // Store initial values on first check
      if (initialAlpha === null || initialBeta === null) {
        initialAlpha = camera.alpha;
        initialBeta = camera.beta;
        return;
      }

      // Check if camera has rotated significantly (more than 0.05 radians ≈ 3 degrees)
      const alphaChanged = Math.abs(camera.alpha - initialAlpha) > 0.05;
      const betaChanged = Math.abs(camera.beta - initialBeta) > 0.05;

      if (alphaChanged || betaChanged) {
        logger.debug('Camera rotation detected - canceling drag-to-look helper');
        this.hasUserDraggedToLook = true;
        this.hideDragToLookHelper();
        this.cancelDragToLookTimeout();
      }
    });
    // Note: No click handlers needed - overlay has pointer-events: none so clicks pass through
  }

  /**
   * Start the 30-second timer for showing drag-to-look helper
   * Called after initial stage load completes
   */
  public startDragToLookTimer(): void {
    // Don't start if user has already dragged
    if (this.hasUserDraggedToLook) return;

    // Cancel any existing timeout
    this.cancelDragToLookTimeout();

    // Start 30 second timer
    this.dragToLookTimeout = setTimeout(() => {
      if (!this.hasUserDraggedToLook) {
        this.showDragToLookHelper();
      }
    }, 30000); // 30 seconds

    logger.debug('Drag-to-look timer started (30s)');
  }

  /**
   * Cancel the drag-to-look timeout
   */
  private cancelDragToLookTimeout(): void {
    if (this.dragToLookTimeout) {
      clearTimeout(this.dragToLookTimeout);
      this.dragToLookTimeout = null;
    }
  }

  /**
   * Show the drag-to-look helper
   */
  private showDragToLookHelper(): void {
    if (!this.dragToLookHelper || this.hasUserDraggedToLook) return;

    logger.debug('Showing drag-to-look helper');
    this.dragToLookHelper.style.display = 'block';
    this.dragToLookHelper.classList.remove('hiding');

    // Auto-hide after 10 seconds if not interacted with
    setTimeout(() => {
      if (!this.hasUserDraggedToLook) {
        this.hideDragToLookHelper();
      }
    }, 10000);
  }

  /**
   * Hide the drag-to-look helper with animation
   */
  private hideDragToLookHelper(): void {
    if (!this.dragToLookHelper) return;

    this.dragToLookHelper.classList.add('hiding');

    // Remove after animation completes
    setTimeout(() => {
      if (this.dragToLookHelper) {
        this.dragToLookHelper.style.display = 'none';
        this.dragToLookHelper.classList.remove('hiding');
      }
    }, 300);
  }

  /**
   * Create floor arrow tooltip element (glassmorphic style)
   */
  private createFloorArrowTooltip(): void {
    this.floorArrowTooltip = document.createElement('div');
    this.floorArrowTooltip.className = 'floor-arrow-tooltip';
    this.floorArrowTooltip.style.cssText = `
      position: fixed;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%);
      color: white;
      padding: 10px 18px;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 0.2px;
      pointer-events: none;
      z-index: 10000;
      display: none;
      white-space: nowrap;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.18);
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    `;

    document.body.appendChild(this.floorArrowTooltip);
  }

  /**
   * Show floor arrow tooltip with stage name
   */
  private showFloorArrowTooltip(targetStageId: string): void {
    if (!this.floorArrowTooltip || !this.showFloorArrowTooltips || !this.experienceConfig) return;

    // Find target stage name
    const targetStage = this.experienceConfig.stages.find(s => s.id === targetStageId);
    if (!targetStage) return;

    const stageName = targetStage.name || targetStage.id;

    this.floorArrowTooltip.textContent = stageName;
    this.floorArrowTooltip.style.display = 'block';

    // Position tooltip at cursor
    this.updateFloorArrowTooltipPosition();
  }

  /**
   * Update floor arrow tooltip position to follow cursor (always above)
   */
  private updateFloorArrowTooltipPosition(): void {
    if (!this.floorArrowTooltip || this.floorArrowTooltip.style.display === 'none') return;

    const x = this.lastMouseX;
    const y = this.lastMouseY;

    // Get tooltip dimensions
    const rect = this.floorArrowTooltip.getBoundingClientRect();
    const tooltipWidth = rect.width;
    const tooltipHeight = rect.height;

    // Position above cursor with offset
    const offsetY = 20; // Distance above cursor
    let posX = x - tooltipWidth / 2;
    let posY = y - tooltipHeight - offsetY;

    // Keep within viewport bounds
    const padding = 10;
    if (posX < padding) posX = padding;
    if (posX + tooltipWidth > window.innerWidth - padding) {
      posX = window.innerWidth - tooltipWidth - padding;
    }
    if (posY < padding) {
      // If no room above, show below cursor instead
      posY = y + offsetY;
    }

    this.floorArrowTooltip.style.left = `${posX}px`;
    this.floorArrowTooltip.style.top = `${posY}px`;
  }

  /**
   * Hide floor arrow tooltip
   */
  private hideFloorArrowTooltip(): void {
    if (!this.floorArrowTooltip) return;
    this.floorArrowTooltip.style.display = 'none';
  }

  /**
   * Toggle audio mute
   */
  private toggleAudioMute(): void {
    if (!this.audioMuteBtn || !this.audioVolumeSlider) return;

    const currentValue = parseInt(this.audioVolumeSlider.value);

    if (currentValue > 0) {
      // Mute
      this.audioVolumeSlider.value = '0';
      this.updateAudioVolume(); // This will set volume to 0
      logger.debug('All audio muted');
    } else {
      // Unmute to 100%
      this.audioVolumeSlider.value = '100';
      this.updateAudioVolume(); // This will set volume to 1.0
      logger.debug('All audio unmuted to 100%');
    }
  }

  /**
   * Update audio volume from slider
   */
  private updateAudioVolume(): void {
    if (!this.audioVolumeSlider || !this.audioMuteBtn) return;

    // Slider 0-100 maps to a 0-1 master multiplier
    const master = parseInt(this.audioVolumeSlider.value) / 100;

    // Each source's effective volume = configured baseline * master
    // This lets creators mix levels in the editor and the viewer slider scales proportionally

    // Stage background audio
    const bgAudio = this.sceneManager.getCurrentAudio();
    if (bgAudio) {
      const baselineVolume = this.experienceConfig?.stages[this.currentStageIndex]?.audioVolume ?? 1.0;
      bgAudio.setVolume(baselineVolume * master);
    }

    // Global tour audio — respects the configured volume as its ceiling
    const globalAudio = this.sceneManager.getGlobalAudio();
    if (globalAudio) {
      const baselineVolume = this.experienceConfig?.globalAudio?.volume ?? 1.0;
      globalAudio.setVolume(baselineVolume * master);
    }

    // Video skybox audio
    const domeComponent = this.sceneManager.getDomeComponent();
    const videoElement = domeComponent.getVideoElement();
    if (videoElement) {
      videoElement.muted = master === 0;
      videoElement.volume = master;
    }

    // Set all hotspot audio volumes (hotspots use master directly; their own volumes are internal)
    const hotspotComponent = this.sceneManager.getHotspotComponent();
    hotspotComponent.setAllAudioVolume(master);

    // Update icon (volume waves)
    if (master === 0) {
      // Muted - no waves
      this.audioMuteBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M3 9v6h4l5 5V4L7 9H3z"/><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>';
    } else if (master < 0.5) {
      // Low volume - one wave
      this.audioMuteBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>';
    } else {
      // High volume - two waves
      this.audioMuteBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>';
    }

    logger.debug(`Master volume: ${(master * 100).toFixed(0)}%`);
  }

  /**
   * Show spatial audio display
   */
  public showSpatialAudio(hotspot: HotspotConfig): void {
    if (!this.spatialAudioTitle) return;

    // Use audioTitle if available, otherwise extract filename
    const displayTitle = hotspot.audioTitle ||
                       hotspot.audioUrl?.split('/').pop()?.split('?')[0] ||
                       'Spatial Audio';

    this.spatialAudioTitle.textContent = '◉ ' + displayTitle;
    this.spatialAudioTitle.style.display = 'block';
    this.currentSpatialAudio = displayTitle;

    // Show the titles display container
    if (this.audioTitlesDisplay) {
      this.audioTitlesDisplay.style.display = 'block';
    }

    // Update overall audio controls visibility
    this.updateAudioDisplay();

    logger.debug(`Showing spatial audio: ${displayTitle}`);
  }

  /**
   * Hide spatial audio display
   */
  public hideSpatialAudio(): void {
    if (!this.spatialAudioTitle) return;

    this.spatialAudioTitle.style.display = 'none';
    this.currentSpatialAudio = null;

    // Update overall audio controls visibility
    this.updateAudioDisplay();

    logger.debug('Hiding spatial audio display');
  }

  /**
   * Update unified audio display visibility
   * Only shows volume controls when audio is actively playing (background, spatial, or video)
   */
  private updateAudioDisplay(): void {
    const hasBackgroundAudio = !!this.currentBackgroundAudio;
    const hasSpatialAudioPlaying = !!this.currentSpatialAudio;
    const hasVideoAudio = !!this.currentVideoAudio;
    // Show volume controls only when audio is actively playing
    const hasActiveAudio = hasBackgroundAudio || hasSpatialAudioPlaying || hasVideoAudio;

    // Show/hide volume controls based on audio actively playing
    if (this.audioControls) {
      if (hasActiveAudio) {
        this.audioControls.classList.remove('hidden');
      } else {
        this.audioControls.classList.add('hidden');
      }
    }

    // Show/hide titles display container
    if (this.audioTitlesDisplay) {
      if (hasActiveAudio) {
        this.audioTitlesDisplay.style.display = 'block';
      } else {
        this.audioTitlesDisplay.style.display = 'none';
      }
    }

    logger.debug(`Audio display updated - Background: ${hasBackgroundAudio}, Spatial: ${hasSpatialAudioPlaying}, Video: ${hasVideoAudio}`);
  }

  /**
   * Get current stage index
   */
  getCurrentStageIndex(): number {
    return this.currentStageIndex;
  }

  /**
   * Get total number of stages
   */
  getTotalStages(): number {
    return this.experienceConfig?.stages.length || 0;
  }

  /**
   * Find a floor arrow hotspot in the source stage that points to the destination stage
   * Returns the hotspot config if found, null otherwise
   */
  private findFloorArrowToStage(sourceStageIndex: number, destStageId: string): HotspotConfig | null {
    if (!this.experienceConfig) return null;
    const sourceStage = this.experienceConfig.stages[sourceStageIndex];
    if (!sourceStage?.hotspots) return null;

    return sourceStage.hotspots.find(h =>
      h.type === 'navigation' &&
      h.portalEffectStyle === 'floor-arrow' &&
      (h.targetSceneId === destStageId || h.targetStageId === destStageId)
    ) || null;
  }

  /**
   * Load a stage directly without portal transition (used when transitions disabled or floor arrow path exists)
   * @param previousIndex - The stage index we're navigating from
   * @param direction - Analytics direction ('jump', 'next', 'previous')
   * @param floorArrowHotspot - Floor arrow hotspot if navigating via floor arrow path, null otherwise
   * @param resetNavEnabled - Whether to call setNavigationEnabled(true) in finally block
   */
  private async loadStageDirectly(
    previousIndex: number,
    direction: 'jump' | 'next' | 'previous',
    floorArrowHotspot: HotspotConfig | null,
    resetNavEnabled: boolean
  ): Promise<void> {
    try {
      this.analyticsService?.trackStageNavigation(
        previousIndex,
        this.currentStageIndex,
        floorArrowHotspot ? 'jump' : direction,
        this.experienceConfig!.stages.length
      );

      await this.loadCurrentStage(null);

      // Apply target view direction if navigating via floor arrow path
      if (floorArrowHotspot?.targetViewDirection) {
        this.orientCameraToTarget(floorArrowHotspot.targetViewDirection);
      }
    } catch (error) {
      logger.error('Stage transition failed:', error);
      this.currentStageIndex = previousIndex;
      this.updateStageCounter();
      if (this.experienceConfig) {
        const prevStage = this.experienceConfig.stages[previousIndex];
        this.updateSceneNameLabel(prevStage.name || prevStage.id);
      }
    } finally {
      this.isLoading = false;
      if (resetNavEnabled) {
        this.setNavigationEnabled(true);
      }
    }
  }

  /**
   * Orient the camera to look at the specified target position
   * Converts a 3D target position to ArcRotateCamera alpha/beta angles
   *
   * For ArcRotateCamera with radius=0, the view direction is:
   *   view.x = -sin(alpha) * sin(beta)
   *   view.z = -cos(alpha) * sin(beta)
   *   view.y = -cos(beta)
   *
   * To look at a target position, we solve for alpha and beta.
   */
  private orientCameraToTarget(target: Vector3Config): void {
    if (this.isInVR()) return;

    const camera = this.scene.activeCamera;

    if (!camera || !(camera instanceof ArcRotateCamera)) {
      return;
    }

    // Create a Vector3 from the target config (scene-local coordinates)
    let targetPos = new Vector3(target.x, target.y, target.z);

    // Rotate target by skybox rotation to get world-space direction
    // (target is in scene-local space, camera is in world space)
    const rotY = this.sceneManager.getSkyboxRotation();
    if (rotY !== 0) {
      const cos = Math.cos(rotY);
      const sin = Math.sin(rotY);
      targetPos = new Vector3(
        targetPos.x * cos - targetPos.z * sin,
        targetPos.y,
        targetPos.x * sin + targetPos.z * cos
      );
    }

    // Calculate alpha (horizontal rotation around Y axis)
    // Babylon.js ArcRotateCamera: alpha=0 looks at +X, alpha=PI/2 looks at +Z
    // To look at direction (x, z), we need alpha = atan2(z, x) then negate for camera direction
    const alpha = Math.atan2(-targetPos.z, -targetPos.x);

    // Calculate beta (vertical angle from Y axis)
    // beta = PI/2 is horizontal, < PI/2 looks down, > PI/2 looks up
    // From view.y = -cos(beta), to look at +y we need beta > PI/2
    const horizontalDist = Math.sqrt(targetPos.x * targetPos.x + targetPos.z * targetPos.z);
    const beta = Math.PI / 2 + Math.atan2(targetPos.y, horizontalDist);

    // Set camera orientation
    camera.alpha = alpha;
    camera.beta = beta;
  }

  /**
   * Dispose UI resources
   */
  dispose(): void {
    this.stopProgressBarUpdates();

    // Remove event listeners (using stored handler references for proper cleanup)
    if (this.prevButton && this.prevButtonHandler) {
      this.prevButton.removeEventListener('click', this.prevButtonHandler);
      this.prevButtonHandler = null;
    }
    if (this.nextButton && this.nextButtonHandler) {
      this.nextButton.removeEventListener('click', this.nextButtonHandler);
      this.nextButtonHandler = null;
    }

    // Clean up navigation event listener
    if (this.minimapClickHandler) {
      document.removeEventListener('navigation:minimapClick', this.minimapClickHandler);
      this.minimapClickHandler = null;
    }

    // Clean up keyboard shortcuts
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
      this.keyboardHandler = null;
    }

    // Dispose navigation manager
    if (this.navigationManager) {
      this.navigationManager.dispose();
      this.navigationManager = null;
    }

    // Clean up stage selector map
    if (this.stageSelectorMap) {
      this.stageSelectorMap.remove();
      this.stageSelectorMap = null;
    }

    // Dispose lead capture forms
    if (this.leadCaptureForm) {
      logger.debug(`Disposing lead capture forms for sequence: ${this.leadFormSequenceId}, session: ${this.leadFormSessionId}`);
      this.leadCaptureForm.dispose();
      this.leadCaptureForm = null;
    }
    if (this.leadCaptureFormVR) {
      this.leadCaptureFormVR.dispose();
      this.leadCaptureFormVR = null;
    }
    this.leadFormSequenceId = null;
    this.leadFormSessionId = null;
  }

  /**
   * Add SVG circular loader to navigation controls
   */
  private addSVGLoader(): void {
    if (!this.navControls || this.navControls.querySelector('.nav-loader-svg')) {
      return; // Already exists
    }

    const loader = document.createElement('div');
    loader.className = 'nav-loader-svg';
    // SVG with rounded rectangle path matching the nav controls shape exactly
    loader.innerHTML = `
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style="position: absolute; top: 0; left: 0; pointer-events: none; overflow: visible;">
        <defs>
          <linearGradient id="nav-loader-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style="stop-color:rgba(255,255,255,0);stop-opacity:0" />
            <stop offset="30%" style="stop-color:rgba(255,255,255,0.6);stop-opacity:0.6" />
            <stop offset="50%" style="stop-color:rgba(255,255,255,1);stop-opacity:1" />
            <stop offset="70%" style="stop-color:rgba(255,255,255,0.6);stop-opacity:0.6" />
            <stop offset="100%" style="stop-color:rgba(255,255,255,0);stop-opacity:0" />
          </linearGradient>
        </defs>
        <rect
          x="1.5"
          y="1.5"
          rx="26"
          ry="26"
          fill="none"
          stroke="url(#nav-loader-gradient)"
          stroke-width="2.5"
          stroke-linecap="round"
          pathLength="400"
          style="width: calc(100% - 3px); height: calc(100% - 3px);"
        />
      </svg>
    `;

    this.navControls.appendChild(loader);
  }

  /**
   * Remove SVG circular loader from navigation controls
   */
  private removeSVGLoader(): void {
    if (!this.navControls) return;

    const loader = this.navControls.querySelector('.nav-loader-svg');
    if (loader) {
      loader.remove();
    }
  }
}
